import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  AlertCircle, Trophy, Calendar, Bell, BellOff, CheckCircle2,
  Sun, Settings, ListTodo, Plus, X, Trash2,
  CloudOff, Cloud, Shield, Download, RefreshCw,
  Target, Camera, Zap, LogOut, CheckSquare, ArrowDownToLine
} from 'lucide-react';

import { useSettings, useAgendaEvents } from './hooks/useLocalStorage';
import { WeeklyAgenda } from './components/agenda/WeeklyAgenda';
import { EventEditorSheet } from './components/agenda/EventEditorSheet';
import { WeekSetupWizard } from './components/agenda/WeekSetupWizard';
import { daysNames, checkFrenchHoliday, generateDailySchedule, getWeekDates, getCategoryStyles } from './data/schedule';

// Services Google
import { useGoogleServices } from './hooks/useGoogleServices';

// Nouveaux hooks
import { useTaskManager } from './hooks/useTaskManager';
import { useTaskProofs } from './hooks/useTaskProofs';
import { useFocusMode } from './hooks/useFocusMode';
import { useMoodTracking } from './hooks/useMoodTracking';
import { useNotifications } from './hooks/useNotifications';
import { useTTS } from './hooks/useTTS';
import { useWindowScrollPosition } from './hooks/useScrollPosition';

// Context
import { ThemeProvider } from './contexts/ThemeContext';
import { AppProvider } from './contexts/AppContext';

// Composants UI
import { BottomSheet } from './components/ui/BottomSheet';
import { TopStatusBar } from './components/layout/TopStatusBar';
import { BottomNavBar } from './components/layout/BottomNavBar';

// Composants depuis les features (architecture modulaire)
import {
  // Gamification
  XPGainAnimation, LevelUpAnimation, BadgeUnlockAnimation, Confetti, EncouragementToast,
  // Rewards (nouveau système de récompenses)
  RewardsPanel, RewardPlayer, RewardsFAB,
  // Proofs
  ProofCaptureModal, VictoryGallery,
  // Focus
  FocusModeOverlay, FocusStartModal,
  // Mood
  MoodCheckInModal,
  // Stats
  StatsView,
  // Crisis
  CrisisModeOverlay,
  // Auth
  GoogleOnboarding, GoogleLoadingScreen, LandingPage,
  // Onboarding
  OnboardingFlow, shouldShowOnboarding,
  // Go Sport
  GoSportFAB, GoSportWizard, GoSportWidget, useGoSport,
  // Block Notes
  BlockNotesView,
  // Subscription
  PricingPage, UpgradeModal,
} from './features';
import { useSubscription } from './hooks/useSubscription';
import { checkStripeReturn } from './services/stripeService';

import { DayHeader } from './components/ui/DayHeader';
import { useWeather } from './hooks/useWeather';
import { UploadProgressToast } from './components/ui/UploadProgressToast';
import { useErrorToast } from './hooks/useErrorToast';
import { captureError, SEVERITY } from './services/errorService';
import { useSyncQueue } from './hooks/useSyncQueue';
import { ConflictResolutionSheet } from './components/ui/ConflictResolutionSheet';
import {
  getConflicts,
  getConflictCount,
  resolveConflict,
  subscribeToConflicts,
} from './services/conflictService';
import {
  saveTask as saveTaskFirestore,
  saveAgendaEvent as saveAgendaEventFirestore,
} from './services/firestoreSync';
import { TaskStartModal } from './components/ui/TaskStartModal';
import { TaskDetailSheet } from './components/ui/TaskDetailSheet';
import { DeviceActionsSheet } from './components/ui/DeviceActionsSheet';
import { EnergyTaskSuggestion } from './components/ui/EnergyTaskSuggestion';
import { SubTasksWithAI } from './components/ui/SubTasksWithAI';
import { UnifiedSettingsSheet } from './features/settings';
import { DayGeneratorSheet } from './components/ui/TaskGeneratorSheet';
import { SetupWizard } from './components/ui/SetupWizard';
import { TemplatesSheet } from './components/ui/TemplatesSheet';
import { useCustomTemplates } from './hooks/useCustomTemplates';
import { AutoGenerationPrompt } from './components/ui/AutoGenerationPrompt';
import { AutoGenerationSettings } from './components/ui/AutoGenerationSettings';
import { useAutoGeneration } from './hooks/useAutoGeneration';
import { DataManagementSheet } from './components/ui/DataManagementSheet';
import { CloudSyncSheet } from './components/ui/CloudSyncSheet';
import { ChallengesPanel } from './components/ui/ChallengesPanel';
import { SwipeableCarousel } from './components/ui/SwipeableCarousel';

// Composants AI et Gamification avancés
import { useAIWeekGenerator } from './hooks/useAIWeekGenerator';
import { CompanionWidget } from './components/agenda/CompanionWidget';
import { QuestPanel } from './components/agenda/QuestPanel';
import { MoodIndicator } from './features/mood/components/MoodIndicator';
import { useServicesStatus } from './hooks/useServicesStatus';
import { useDeviceActions } from './hooks/useDeviceActions';
import { useAccessibility } from './hooks/useAccessibility';
import { DashboardPage } from './pages/DashboardPage';

// Composant Toggle pour les paramètres
const SettingToggle = ({ icon: Icon, active, label, description, checked, onChange }) => (
  <label className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-md cursor-pointer hover:bg-zinc-800/50 transition-all">
    <div className="flex items-center gap-3">
      <Icon className={`w-5 h-5 ${active ? 'text-zinc-300' : 'text-zinc-500'}`} />
      <div>
        <span className="text-sm font-medium text-zinc-200">{label}</span>
        <span className="text-xs text-zinc-500 block">{description}</span>
      </div>
    </div>
    <div className="relative">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="sr-only peer"
      />
      <div className="w-11 h-6 bg-zinc-700 rounded-full peer peer-checked:bg-[var(--theme-accent,#3b82f6)] transition-colors" />
      <div className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
    </div>
  </label>
);

// Wrapper principal avec navigation et gestion mode local
function App() {
  const [showLanding, setShowLanding] = useState(() => {
    const hasVisited = localStorage.getItem('adhd_has_visited');
    // Si déjà connecté à Google, ne pas montrer la landing
    const savedAuth = localStorage.getItem('adhd-planner-google-auth');
    if (savedAuth) {
      try {
        const parsed = JSON.parse(savedAuth);
        const token = parsed.token;
        const expiresIn = token.expires_in || 3600;
        const expiresAt = token.savedAt + (expiresIn * 1000);
        if (Date.now() < expiresAt - 60000) {
          return false; // Token valide, skip landing
        }
      } catch (e) {}
    }
    return !hasVisited;
  });

  const [isLocalMode, setIsLocalMode] = useState(() => {
    return localStorage.getItem('adhd_local_mode') === 'true';
  });

  const [showOnboarding, setShowOnboarding] = useState(() => shouldShowOnboarding());

  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  // Google auth pour la landing page
  const google = useGoogleServices();

  const handleEnterApp = useCallback(() => {
    localStorage.setItem('adhd_has_visited', 'true');
    setShowLanding(false);
  }, []);

  const handleBackToLanding = useCallback(() => {
    setShowLanding(true);
  }, []);

  const handleEnableLocalMode = useCallback(() => {
    localStorage.setItem('adhd_local_mode', 'true');
    setIsLocalMode(true);
  }, []);

  const handleDisableLocalMode = useCallback(() => {
    localStorage.removeItem('adhd_local_mode');
    setIsLocalMode(false);
  }, []);

  const handleGoogleSignIn = useCallback(async () => {
    setIsGoogleLoading(true);
    try {
      await google.signIn();
      // Connexion réussie, entrer dans l'app
      handleEnterApp();
    } catch (error) {
      captureError(error, 'Google sign-in (landing)', SEVERITY.USER_FACING);
    } finally {
      setIsGoogleLoading(false);
    }
  }, [google, handleEnterApp]);

  if (showLanding) {
    return (
      <LandingPage
        onEnterApp={handleEnterApp}
        onGoogleSignIn={handleGoogleSignIn}
        isGoogleConnected={google.isSignedIn}
        googleUser={google.user}
        isGoogleLoading={isGoogleLoading || google.isInitializing}
      />
    );
  }

  if (showOnboarding) {
    return (
      <ThemeProvider>
        <OnboardingFlow onComplete={() => setShowOnboarding(false)} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <MainApp
        onBackToLanding={handleBackToLanding}
        isLocalMode={isLocalMode}
        onEnableLocalMode={handleEnableLocalMode}
        onDisableLocalMode={handleDisableLocalMode}
        sharedGoogle={google}
      />
    </ThemeProvider>
  );
}

function MainApp({ onBackToLanding, isLocalMode, onEnableLocalMode, onDisableLocalMode, sharedGoogle }) {
  // Accessibility — initialized early so CSS classes are applied immediately
  useAccessibility(); // eslint-disable-line no-unused-vars

  const [weekOffset, setWeekOffset] = useState(0);
  const [currentDayIndex, setCurrentDayIndex] = useState(() => {
    const day = new Date().getDay();
    return day === 0 ? 6 : day - 1;
  });

  // Services Google centralisés - utilise le hook partagé depuis App
  const google = sharedGoogle;

  // Gestionnaire de tâches unifié (compose useCustomTasks + useCompletedTasks + useGameification + useRewardsSystem)
  const {
    tasks: customTasks,
    addTask,
    deleteTask: removeTask,
    getTasksForDate,
    clearAllTasks,
    toggleTask, // eslint-disable-line no-unused-vars
    toggleSubTask,
    isCompleted,
    isSubTaskCompleted,
    getCompletedCount,
    isTaskCompleted,
    completeTask: completeTaskAction,
    finalizeCompletion,
    gameification,
    rewards,
  } = useTaskManager({ weekOffset, currentDayIndex });

  // Agenda hebdomadaire (nouveau)
  const agendaEvents = useAgendaEvents();
  const [activeView, setActiveView] = useState(() => {
    try { return localStorage.getItem('adhd-active-view') || 'dashboard'; } catch { return 'dashboard'; }
  }); // 'dashboard' | 'daily' | 'weekly'

  // Persist view choice
  const setActiveViewPersisted = useCallback((view) => {
    setActiveView(view);
    try { localStorage.setItem('adhd-active-view', view); } catch {}
  }, []);
  const [editingAgendaEvent, setEditingAgendaEvent] = useState(null);
  const [showEventEditor, setShowEventEditor] = useState(false);
  const { settings, toggleSetting } = useSettings();

  const { proofs, addProof, deleteProof, getProofsForTask } = useTaskProofs();
  const focusMode = useFocusMode();
  const mood = useMoodTracking();
  const { announce, ttsSettings, updateTTS, voices } = useTTS();
  const notifications = useNotifications({ onAnnounce: announce });

  // Persist window scroll position for each page-level view
  useWindowScrollPosition('daily',  activeView === 'daily');
  useWindowScrollPosition('dashboard', activeView === 'dashboard');
  const weatherData = useWeather();
  const { toastError, dismissToast } = useErrorToast();
  const { pendingCount: pendingSyncOps } = useSyncQueue();

  // Hook IA pour compagnon et quêtes
  const aiPlanner = useAIWeekGenerator();

  // Hook de statut des services (Gemini, Calendar, Firebase)
  const servicesStatus = useServicesStatus();

  // Hook pour les actions appareil (minuteurs, alarmes, musique)
  const deviceActions = useDeviceActions();

  // Hook Go Sport
  const goSport = useGoSport();

  // États locaux
  const [activeTaskId, setActiveTaskId] = useState(null);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState(null); // Pour le TaskDetailSheet
  const [timeLeft, setTimeLeft] = useState("");
  const [progressPercent, setProgressPercent] = useState(0);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showDayGenerator, setShowDayGenerator] = useState(false);
  const [showWeekSetupWizard, setShowWeekSetupWizard] = useState(false);
  const [showChallenges, setShowChallenges] = useState(false);
  const [showDeviceActions, setShowDeviceActions] = useState(false);
  const [showConflictSheet, setShowConflictSheet] = useState(false);
  const [conflicts, setConflicts] = React.useState(() => getConflicts());

  // Wizard de première utilisation
  const [showSetupWizard, setShowSetupWizard] = useState(() => {
    const setupCompleted = localStorage.getItem('adhd_setup_completed');
    return !setupCompleted;
  });

  // Templates personnalisés
  const [showTemplates, setShowTemplates] = useState(false);
  const customTemplates = useCustomTemplates();

  // Génération automatique
  const [showAutoGenSettings, setShowAutoGenSettings] = useState(false);
  const autoGeneration = useAutoGeneration();

  // Gestion des données
  const [showDataManagement, setShowDataManagement] = useState(false);
  const [showCloudSync, setShowCloudSync] = useState(false);

  // Note: Les événements Google Calendar sont maintenant sauvegardés directement dans customTasks (Firestore)
  // au lieu d'être stockés dans un Map local qui était perdu au rechargement

  // Nouveaux états pour les modales et animations
  const [showProofCapture, setShowProofCapture] = useState(null); // tâche pour laquelle capturer
  const [pendingTaskCompletion, setPendingTaskCompletion] = useState(null); // tâche en attente de preuve pour complétion
  const [showVictoryGallery, setShowVictoryGallery] = useState(false);
  const [showFocusStart, setShowFocusStart] = useState(null); // tâche pour focus
  const [showStats, setShowStats] = useState(false);
  const [showCrisisMode, setShowCrisisMode] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentEncouragement, setCurrentEncouragement] = useState(null);
  const [completingTaskId, setCompletingTaskId] = useState(null);

  // ── Abonnement ──────────────────────────────────────────────────
  const subscription = useSubscription(sharedGoogle?.user?.uid || null);
  const [upgradeFeature, setUpgradeFeature] = useState(null);

  // Vérifier retour depuis Stripe Checkout
  useEffect(() => {
    const result = checkStripeReturn();
    if (result === 'success') {
      setCurrentEncouragement({
        message: '🎉 Bienvenue dans ADHD Planner Pro ! Toutes les fonctionnalités sont débloquées.',
        type: 'success',
      });
      setTimeout(() => setCurrentEncouragement(null), 6000);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Pour l'animation
  const [uploadStatus, setUploadStatus] = useState(null); // Pour le toast d'upload
  const [currentTime, setCurrentTime] = useState(new Date()); // Pour l'indicateur de temps actuel
  const [taskToStart, setTaskToStart] = useState(null); // Tâche en attente de démarrage
  const [lastNotifiedTaskId, setLastNotifiedTaskId] = useState(null); // Pour éviter les notifications répétées

  const [newTask, setNewTask] = useState({
    task: "", time: "14:00", duration: 60, category: "chore", desc: "", instructions: [""]
  });
  const [isSyncingCalendar, setIsSyncingCalendar] = useState(false);

  // Calcul des dates de la semaine
  const weekDates = useMemo(() => getWeekDates(weekOffset), [weekOffset]);
  const selectedDate = weekDates[currentDayIndex];
  const holiday = selectedDate ? checkFrenchHoliday(selectedDate) : null;
  const isWeekend = currentDayIndex >= 5;
  const isNonWorkable = isWeekend || !!holiday;

  // Helper pour formater la date en local (même format que WeeklyAgenda)
  const formatDateKey = (d) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Helper pour extraire le texte d'une instruction (peut être string ou objet)
  const getInstructionText = (instruction) => {
    if (!instruction) return '';
    if (typeof instruction === 'string') return instruction;
    if (typeof instruction === 'object' && instruction.text) return instruction.text;
    if (typeof instruction === 'object' && instruction.name) return instruction.name;
    return '';
  };

  // ═══ Helper: créer un agendaEvent à partir d'un customTask ═══
  const bridgeTaskToAgendaEvent = useCallback((taskData) => {
    if (!taskData.time || !taskData.date) return;
    const [h, m] = (taskData.time || '09:00').split(':').map(Number);
    const dur = taskData.duration || 30;
    const endMin = h * 60 + m + dur;
    const endH = String(Math.floor(endMin / 60) % 24).padStart(2, '0');
    const endM = String(endMin % 60).padStart(2, '0');

    // Anti-doublon : vérifier par googleEventId, linkedTaskId, OU titre+date+heure
    const gid = taskData.googleEventId;
    const existing = (agendaEvents.events || []).find(e => {
      if (gid && e.googleEventId === gid) return true;
      if (taskData.id && e.linkedTaskId === taskData.id) return true;
      return e.date === taskData.date && e.title === taskData.task && e.start === taskData.time;
    });
    if (existing) return;

    agendaEvents.addEvent({
      title: taskData.task,
      date: taskData.date,
      start: taskData.time,
      end: `${endH}:${endM}`,
      category: taskData.category || 'chore',
      subTasks: (taskData.instructions || []).map(i => ({
        text: typeof i === 'string' ? i : (i.text || i.name || ''),
        completed: false,
      })),
      isImported: true,
      linkedTaskId: taskData.id || null,
      googleEventId: taskData.googleEventId || null,
    });
  }, [agendaEvents]);

  // ═══ Helper: créer un customTask à partir d'un agendaEvent (reverse bridge) ═══
  const bridgeEventToTask = useCallback((event) => {
    if (!event.start || !event.date) return;

    // Anti-doublon : vérifier par linkedTaskId, ID, ou titre+date+heure
    const existing = (customTasks || []).find(t => {
      if (event.linkedTaskId && t.id === event.linkedTaskId) return true;
      if (event.googleEventId && t.googleEventId === event.googleEventId) return true;
      return t.date === event.date && t.task === event.title && (t.time === event.start || t.startTime === event.start);
    });
    if (existing) return;

    // Calculer la durée à partir de start/end
    const [sh, sm] = (event.start || '09:00').split(':').map(Number);
    const [eh, em] = (event.end || '10:00').split(':').map(Number);
    const dur = Math.max((eh * 60 + em) - (sh * 60 + sm), 15);

    addTask({
      task: event.title || 'Événement',
      time: event.start,
      duration: dur,
      date: event.date,
      category: event.category || 'chore',
      desc: '',
      instructions: (event.subTasks || []).map(s => typeof s === 'string' ? s : (s.text || '')).filter(Boolean),
      iconName: 'Calendar',
      points: 15,
      isImported: true, // empêche le re-sync vers Google Calendar
      linkedEventId: event.id || null,
    });
  }, [customTasks, addTask]);

  // ═══ Sync bidirectionnelle: détecter les changements dans agendaEvents ═══
  const handleEventsChange = useCallback((newEventsOrUpdater) => {
    // D'abord mettre à jour les agendaEvents
    agendaEvents.setEvents(newEventsOrUpdater);

    // Ensuite, syncer les nouveaux événements vers customTasks
    const newEvents = typeof newEventsOrUpdater === 'function'
      ? newEventsOrUpdater(agendaEvents.events)
      : newEventsOrUpdater;

    // Trouver les événements qui n'ont pas de customTask correspondant
    const currentLinked = new Set((customTasks || []).filter(t => t.linkedEventId).map(t => t.linkedEventId));

    (newEvents || []).forEach(event => {
      // Skip les événements déjà bridgés (créés depuis customTasks)
      if (event.isImported && event.linkedTaskId) return;
      // Skip si déjà un customTask lié
      if (currentLinked.has(event.id)) return;
      // Créer le customTask
      bridgeEventToTask(event);
    });
  }, [agendaEvents, customTasks, bridgeEventToTask]);

  const handleDeleteEvent = useCallback((eventId) => {
    // Trouver l'événement avant suppression
    const event = (agendaEvents.events || []).find(e => e.id === eventId);
    // Supprimer l'agendaEvent
    agendaEvents.deleteEvent(eventId);
    // Supprimer le customTask lié s'il existe
    if (event?.linkedTaskId) {
      removeTask(event.linkedTaskId);
    }
    // Ou chercher par linkedEventId
    const linkedTask = (customTasks || []).find(t => t.linkedEventId === eventId);
    if (linkedTask) {
      removeTask(linkedTask.id);
    }
  }, [agendaEvents, customTasks, removeTask]);

  // ═══ Sync Google Calendar: importer TOUS les calendriers (semaine entière) + push local ═══
  const syncGoogleCalendar = useCallback(async () => {
    if (!google.isSignedIn || !selectedDate) {
      setCurrentEncouragement({ message: '⚠️ Connectez-vous à Google pour synchroniser', type: 'warning' });
      setTimeout(() => setCurrentEncouragement(null), 3000);
      return;
    }

    setIsSyncingCalendar(true);
    let importedCount = 0;
    let pushedCount = 0;

    try {
      const calService = google.services.calendar;

      // ── Construire l'index global de TOUS les googleEventId connus ──
      const allKnownGoogleIds = new Set(
        (customTasks || []).filter(t => t.googleEventId).map(t => t.googleEventId)
      );

      // ── Syncer le mois en cours (pas juste la semaine) ──
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const totalDays = monthEnd.getDate();

      for (let d = 0; d < totalDays; d++) {
        const dayDate = new Date(monthStart);
        dayDate.setDate(dayDate.getDate() + d);
        const dateStr = formatDateKey(dayDate);

        // Effacer le cache pour forcer la re-sync
        localStorage.removeItem(`google_sync_${dateStr}`);
        localStorage.removeItem(`google_sync_error_${dateStr}`);

        // ── PULL: importer depuis TOUS les calendriers ──
        let allEvents;
        try {
          allEvents = await calService.getAllCalendarEvents(dayDate);
        } catch {
          continue; // Skip ce jour en cas d'erreur
        }

        for (const event of allEvents) {
          // Anti-doublon robuste: vérifier par googleEventId brut
          if (allKnownGoogleIds.has(event.id)) continue;

          const start = event.start?.dateTime || event.start?.date;
          const end = event.end?.dateTime || event.end?.date;
          if (!start) continue;

          const startDate = new Date(start);
          const endDate = new Date(end);
          const durationMinutes = Math.round((endDate - startDate) / 60000);
          if (durationMinutes <= 0) continue;

          // ID stable: basé sur googleEventId (pas de Date.now())
          const stableId = `google-${event.id}`;

          const taskData = {
            id: stableId,
            task: event.summary || 'Événement sans titre',
            title: event.summary || 'Événement sans titre',
            time: startDate.toTimeString().slice(0, 5),
            duration: durationMinutes,
            date: dateStr,
            desc: event.description || '',
            category: calService.getCategoryFromEvent(event),
            iconName: calService.getIconFromEvent(event),
            isImported: true,
            googleEventId: event.id,
            location: event.location || '',
            calendarName: event._calendarName || 'Principal',
            instructions: [],
          };

          addTask(taskData);
          bridgeTaskToAgendaEvent(taskData);
          allKnownGoogleIds.add(event.id); // Marquer comme connu pour éviter les doublons intra-sync
          importedCount++;
        }

        // Marquer la date synchronisée
        localStorage.setItem(`google_sync_${dateStr}`, Date.now().toString());

        // ── PUSH: envoyer les tâches locales vers Google Calendar ──
        const localTasks = (getTasksForDate(dateStr) || []).filter(t => !t.isImported);
        if (localTasks.length > 0) {
          try {
            const pushResult = await calService.pushTasksToCalendar(localTasks, dayDate);
            pushedCount += pushResult.pushed;
          } catch { /* continue */ }
        }
      }

      // ── Message résultat ──
      const parts = [];
      if (importedCount > 0) parts.push(`${importedCount} importé(s)`);
      if (pushedCount > 0) parts.push(`${pushedCount} envoyé(s)`);

      setCurrentEncouragement({
        message: parts.length > 0
          ? `📅 Sync semaine réussie ! ${parts.join(', ')}`
          : '✓ Tout est déjà synchronisé',
        type: parts.length > 0 ? 'success' : 'info'
      });

    } catch (err) {
      captureError(err, 'sync Calendar', SEVERITY.BACKGROUND);
      setCurrentEncouragement({ message: '❌ Erreur de synchronisation', type: 'error' });
    } finally {
      setIsSyncingCalendar(false);
      setTimeout(() => setCurrentEncouragement(null), 4000);
    }
  }, [google, selectedDate, weekDates, customTasks, getTasksForDate, addTask, bridgeTaskToAgendaEvent]);

  // ═══ Fusion customTasks + agendaEvents pour la vue semaine ═══
  const mergedWeeklyEvents = useMemo(() => {
    const agendaEvs = agendaEvents.events || [];
    const agendaIndex = new Set();
    agendaEvs.forEach(e => {
      if (e.linkedTaskId) agendaIndex.add(e.linkedTaskId);
      if (e.googleEventId) agendaIndex.add(`gid:${e.googleEventId}`);
    });

    // Convertir les customTasks qui n'ont PAS déjà un agendaEvent lié
    const taskEvents = (customTasks || [])
      .filter(t => t.date && t.time)
      .filter(t => {
        if (agendaIndex.has(t.id)) return false;
        if (t.googleEventId && agendaIndex.has(`gid:${t.googleEventId}`)) return false;
        // Vérification par titre+date+heure
        return !agendaEvs.some(e =>
          e.date === t.date && e.title === t.task && e.start === t.time
        );
      })
      .map(t => {
        const [h, m] = (t.time || '09:00').split(':').map(Number);
        const dur = t.duration || 30;
        const endMin = h * 60 + m + dur;
        return {
          id: `task-${t.id}`,
          date: t.date,
          start: t.time,
          end: `${String(Math.floor(endMin / 60) % 24).padStart(2, '0')}:${String(endMin % 60).padStart(2, '0')}`,
          title: t.task,
          category: t.category || 'chore',
          subTasks: (t.instructions || []).map(i => ({
            text: typeof i === 'string' ? i : (i.text || i.name || ''),
            completed: false,
          })),
          completed: isTaskCompleted?.(currentDayIndex, t) || false,
          isFromTask: true,
          linkedTaskId: t.id || null,
          googleEventId: t.googleEventId || null,
          location: t.location || '',
          calendarName: t.calendarName || '',
          isImported: t.isImported || false,
          isAIGenerated: t.isAIGenerated || false,
        };
      });

    return [...agendaEvs, ...taskEvents];
  }, [agendaEvents.events, customTasks, isTaskCompleted, currentDayIndex]);

  // Génération du planning avec groupement par heure
  const currentSchedule = useMemo(() => {
    if (!selectedDate) return [];
    const dateStr = formatDateKey(selectedDate); // Format local, pas UTC !
    const dayCustomTasks = getTasksForDate(dateStr);

    // Les événements Google sont maintenant dans customTasks (avec isImported: true)
    // Pas besoin de les récupérer séparément

    // Récupérer les événements de l'agenda hebdomadaire pour cette date
    const dayAgendaEvents = (agendaEvents.events || []).filter(e => e.date === dateStr).map(event => {
      // Calculer la durée en minutes
      const [startH, startM] = (event.start || '09:00').split(':').map(Number);
      const [endH, endM] = (event.end || '10:00').split(':').map(Number);
      const durationMinutes = (endH * 60 + endM) - (startH * 60 + startM);

      return {
        id: event.id,
        task: event.title || 'Événement',
        time: event.start || '09:00',
        endTime: event.end || null, // Heure de fin exacte pour les événements agenda
        duration: durationMinutes > 0 ? durationMinutes : 60,
        category: event.category || 'default',
        desc: '',
        instructions: event.subTasks || [],
        isAgendaEvent: true, // Marqueur pour identifier comme événement agenda
        completed: event.completed || false,
        iconName: 'Calendar',
        points: 15
      };
    });

    // Générer le planning de base
    const baseSchedule = generateDailySchedule(currentDayIndex, {
      holiday,
      customTasks: dayCustomTasks
    });

    // Fusionner avec les événements de l'agenda hebdomadaire
    // (Les événements Google sont déjà dans baseSchedule via dayCustomTasks)
    const combinedSchedule = [...baseSchedule, ...dayAgendaEvents];

    // Grouper par heure pour permettre la superposition
    const groupedByTime = {};
    combinedSchedule.forEach(task => {
      if (!groupedByTime[task.time]) {
        groupedByTime[task.time] = [];
      }
      groupedByTime[task.time].push(task);
    });

    // Convertir en tableau et trier par heure
    return Object.entries(groupedByTime)
      .sort(([timeA], [timeB]) => timeA.localeCompare(timeB))
      .map(([time, tasks]) => ({
        time,
        tasks: tasks.sort((a, b) => {
          // Les événements agenda et Google en dernier pour qu'ils apparaissent en superposition
          if ((a.isImported || a.isAgendaEvent) && !(b.isImported || b.isAgendaEvent)) return 1;
          if (!(a.isImported || a.isAgendaEvent) && (b.isImported || b.isAgendaEvent)) return -1;
          return 0;
        })
      }));
  }, [currentDayIndex, selectedDate, holiday, getTasksForDate, agendaEvents.events]);

  // Importer les événements quand on change de jour ou de semaine
  // Note: customTasks retiré des deps pour éviter boucle infinie avec Firestore
  // Subscribe to conflict-store changes
  React.useEffect(() => {
    const unsub = subscribeToConflicts(updated => setConflicts({ ...updated }));
    return unsub;
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const importCalendarEvents = async () => {
      // Vérifier que Calendar est vraiment disponible
      if (!google.isSignedIn || !google.servicesStatus.calendar || !selectedDate) {
        return;
      }

      // Vérifier que GAPI Calendar API est chargée
      if (!window.gapi?.client?.calendar?.events?.list) {
        return;
      }

      const dateStr = formatDateKey(selectedDate);
      const cacheKey = `google_sync_${dateStr}`;
      const errorKey = `google_sync_error_${dateStr}`;
      const now = Date.now();

      // Éviter de re-syncer la même date trop souvent (cache de 5 minutes)
      const lastSync = localStorage.getItem(cacheKey);
      if (lastSync && (now - parseInt(lastSync)) < 5 * 60 * 1000) {
        return; // Déjà synchronisé récemment
      }

      // TTL par type d'erreur :
      //   403 (API non activée) → 24 heures (ne pas spammer — activer l'API manuellement)
      //   404 (API inactive)    → 1 heure
      //   401 (auth expirée)    → 5 minutes
      //   429 (rate limit)      → backoff exponentiel
      //   autres / réseau       → 2 minutes
      const errorRaw = localStorage.getItem(errorKey);
      if (errorRaw) {
        try {
          const { timestamp, status, retryCount = 0 } = JSON.parse(errorRaw);
          let ttlMs;
          if (status === 403) {
            ttlMs = 24 * 60 * 60 * 1000; // 24 heures — API non activée
          } else if (status === 404) {
            ttlMs = 60 * 60 * 1000; // 1 heure
          } else if (status === 401) {
            ttlMs = 5 * 60 * 1000;  // 5 minutes
          } else if (status === 429) {
            ttlMs = Math.min(Math.pow(2, retryCount) * 2 * 60 * 1000, 32 * 60 * 1000);
          } else {
            ttlMs = 2 * 60 * 1000; // 2 minutes
          }
          if (now - timestamp < ttlMs) return;
        } catch {
          // Ancien format (timestamp brut) — appliquer 1h par défaut
          if (now - parseInt(errorRaw) < 60 * 60 * 1000) return;
        }
      }

      // Vérifier le flag global 403 (API non activée — ne pas réessayer)
      const global403 = localStorage.getItem('google_calendar_api_disabled');
      if (global403 && (now - parseInt(global403)) < 24 * 60 * 60 * 1000) {
        return; // Calendar API non activée, skip pour 24h
      }

      try {
        // Importer depuis TOUS les calendriers (pas juste primary)
        const calService = google.services.calendar;
        const googleEvents = await calService.getAllCalendarEvents(selectedDate);

        if (isCancelled) return;

        // Anti-doublon: collecter TOUS les googleEventId déjà connus
        const currentTasks = getTasksForDate(dateStr) || [];
        const existingGoogleIds = new Set(
          currentTasks.filter(t => t.googleEventId).map(t => t.googleEventId)
        );
        const existingIds = new Set(currentTasks.map(t => t.id));

        let importedCount = 0;
        for (const event of googleEvents) {
          if (isCancelled) break;

          const stableId = `google-${event.id}`;
          if (existingGoogleIds.has(event.id) || existingIds.has(stableId)) continue;

          const start = event.start?.dateTime || event.start?.date;
          const end = event.end?.dateTime || event.end?.date;
          if (!start) continue;
          const startDate = new Date(start);
          const endDate = new Date(end);
          const durationMinutes = Math.round((endDate - startDate) / 60000);
          if (durationMinutes <= 0) continue;

          const taskData = {
            id: stableId,
            task: event.summary || 'Événement sans titre',
            title: event.summary || 'Événement sans titre',
            time: startDate.toTimeString().slice(0, 5),
            duration: durationMinutes,
            date: dateStr,
            desc: event.description || '',
            category: calService.getCategoryFromEvent(event),
            iconName: calService.getIconFromEvent(event),
            isImported: true,
            googleEventId: event.id,
            location: event.location || '',
            calendarName: event._calendarName || 'Principal',
            instructions: [],
          };
          addTask(taskData);
          bridgeTaskToAgendaEvent(taskData);
          existingGoogleIds.add(event.id);
          importedCount++;
        }

        if (importedCount > 0) {
          // importedCount événements Google importés
        }

        // Marquer la date comme synchronisée
        localStorage.setItem(cacheKey, now.toString());
        localStorage.removeItem(errorKey);
        // Effacer le flag global 403 si la sync a réussi
        localStorage.removeItem('google_calendar_api_disabled');

      } catch (err) {
        const status = err?.status || err?.result?.error?.code;
        if (status) {
          // 403 = API non activée → flag global, arrêt total
          if (status === 403) {
            localStorage.setItem('google_calendar_api_disabled', now.toString());
            console.warn('🛑 Calendar API 403 — désactivation globale pour 24h');
            return;
          }

          let retryCount = 0;
          try {
            const prev = JSON.parse(localStorage.getItem(errorKey) || '{}');
            retryCount = (prev.retryCount || 0) + (status === 429 ? 1 : 0);
          } catch { /* ignore */ }

          captureError(new Error(`Calendar API erreur ${status}`), 'import Calendar', SEVERITY.BACKGROUND);
          localStorage.setItem(errorKey, JSON.stringify({ timestamp: now, status, retryCount }));
        } else {
          captureError(err, 'import Calendar', SEVERITY.BACKGROUND);
        }
      }
    };

    importCalendarEvents();

    return () => {
      isCancelled = true;
    };
  }, [google.isSignedIn, google.servicesStatus.calendar, selectedDate, addTask, getTasksForDate, bridgeTaskToAgendaEvent]);

  // Vérifier la génération automatique au chargement
  useEffect(() => {
    // Ne pas déclencher si le wizard est ouvert ou si on vient de lancer l'app
    if (showSetupWizard) return;

    const timer = setTimeout(() => {
      const result = autoGeneration.triggerAutoGeneration(customTemplates.applyTemplate);

      // Si mode auto et résultat, appliquer les tâches
      if (result && result !== 'pending' && typeof result === 'object') {
        Object.entries(result).forEach(([dateStr, tasks]) => {
          tasks.forEach(task => {
            addTask({
              ...task,
              date: dateStr,
              isGenerated: true
            });
          });
        });
        setCurrentEncouragement({
          message: '🎉 Ta semaine a été générée automatiquement !',
          type: 'success'
        });
        setTimeout(() => setCurrentEncouragement(null), 4000);
      }
    }, 1500); // Délai pour laisser l'app se charger

    return () => clearTimeout(timer);
  }, []); // Exécuter une seule fois au montage

  // Timer temps réel
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      if (weekOffset === 0) {
        // const dayIdx = now.getDay() === 0 ? 6 : now.getDay() - 1;
        
        // Récupérer le planning actuel avec la nouvelle structure
        let current = null;
        for (const timeGroup of currentSchedule) {
          for (const task of timeGroup.tasks) {
            const [h, m] = task.time.split(':').map(Number);
            const start = new Date(now);
            start.setHours(h, m, 0, 0);
            const end = new Date(start.getTime() + (task.duration || 30) * 60000);
            if (now >= start && now < end) {
              current = task;
              break;
            }
          }
          if (current) break;
        }

        if (current) {
          // Nouvelle tâche détectée
          if (current.id !== activeTaskId) {
            setActiveTaskId(current.id);

            // Vérifier si la tâche est complétée (inline pour éviter "used before defined")
            const checkTaskCompleted = (task) => {
              if (!task.instructions || task.instructions.length === 0) {
                return isCompleted(weekOffset, currentDayIndex, task.id);
              }
              const checked = getCompletedCount(weekOffset, currentDayIndex, task.id, task.instructions.length);
              return checked > task.instructions.length / 2;
            };

            // Notification et modal pour nouvelle tâche (éviter les doublons)
            if (current.id !== lastNotifiedTaskId && !checkTaskCompleted(current)) {
              setLastNotifiedTaskId(current.id);

              // Envoyer notification système
              if (notifications.enabled && notifications.permission === 'granted') {
                notifications.sendNotification(current, 'at');
              }

              // Afficher le modal de démarrage
              setTaskToStart(current);
            }
          }

          const [h, m] = current.time.split(':').map(Number);
          const start = new Date(now);
          start.setHours(h, m, 0, 0);
          const end = new Date(start.getTime() + (current.duration || 30) * 60000);
          const diff = end - now;
          setTimeLeft(`${Math.floor(diff / 60000)}m ${Math.floor((diff % 60000) / 1000)}s`);
          setProgressPercent(Math.min(((now - start) / (end - start)) * 100, 100));
        } else {
          if (activeTaskId) setActiveTaskId(null);
          setTimeLeft("Repos");
          setProgressPercent(0);
        }
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [activeTaskId, weekOffset, weekDates, currentSchedule, currentDayIndex, lastNotifiedTaskId, notifications, isCompleted, getCompletedCount]);

  // Timer pour l'indicateur de l'heure actuelle (mise à jour chaque minute)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Mise à jour chaque minute
    return () => clearInterval(timer);
  }, []);

  // Programmer les notifications pour les tâches du jour
  useEffect(() => {
    if (!notifications.enabled || weekOffset !== 0) return;

    // Vérifier qu'on est sur le jour actuel
    const today = new Date();
    const todayIndex = today.getDay() === 0 ? 6 : today.getDay() - 1;
    if (currentDayIndex !== todayIndex) return;

    // Programmer les notifications pour chaque tâche non complétée
    currentSchedule.forEach(timeGroup => {
      timeGroup.tasks.forEach(task => {
        // Ne pas notifier les tâches déjà complétées ou importées
        if (task.isImported || isTaskCompleted(currentDayIndex, task)) return;

        // Programmer la notification
        notifications.scheduleNotification(task, task.time);
      });
    });

    // Cleanup: annuler toutes les notifications au démontage
    return () => {
      currentSchedule.forEach(timeGroup => {
        timeGroup.tasks.forEach(task => {
          notifications.cancelNotifications(task.id);
        });
      });
    };
  }, [currentSchedule, currentDayIndex, weekOffset, notifications, isTaskCompleted]);

  // Calcul de la progression journalière
  const dailyProgress = useMemo(() => {
    if (currentSchedule.length === 0) return { completed: 0, total: 0, percent: 0 };

    let total = 0;
    let completed = 0;

    currentSchedule.forEach(timeGroup => {
      timeGroup.tasks.forEach(task => {
        total++;
        if (isTaskCompleted(currentDayIndex, task)) {
          completed++;
        }
      });
    });

    return {
      completed,
      total,
      percent: total > 0 ? Math.round((completed / total) * 100) : 0
    };
  }, [currentSchedule, currentDayIndex, isTaskCompleted]);

  // Prochaine tâche à venir
  const nextUpcomingTask = useMemo(() => {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    for (const timeGroup of currentSchedule) {
      for (const task of timeGroup.tasks) {
        if (task.time > currentTime && !isTaskCompleted(currentDayIndex, task)) {
          return task;
        }
      }
    }
    return null;
  }, [currentSchedule, currentDayIndex, isTaskCompleted]);

  // Formater la durée de manière rationnelle
  const formatDuration = (minutes) => {
    if (!minutes || minutes <= 0) return '~30min';
    if (minutes < 15) return '~15min';
    if (minutes <= 20) return '~20min';
    if (minutes <= 35) return '~30min';
    if (minutes <= 50) return '~45min';
    if (minutes <= 75) return '~1h';
    if (minutes <= 105) return '~1h30';
    if (minutes <= 135) return '~2h';
    if (minutes <= 165) return '~2h30';
    if (minutes <= 195) return '~3h';
    return `~${Math.round(minutes / 60)}h`;
  };

  // Handler pour démarrer une tâche (enregistrer l'heure exacte de début)
  const handleTaskStart = useCallback((taskId, actualStartTime) => {
    // Mettre à jour l'événement avec l'heure de début réelle
    const task = currentSchedule.flatMap(tg => tg.tasks).find(t => t.id === taskId);

    if (task) {
      if (task.isAgendaEvent) {
        // Mettre à jour l'événement agenda avec l'heure de début réelle
        agendaEvents.updateEvent(taskId, {
          actualStartTime: actualStartTime,
          startedAt: actualStartTime
        });
      } else if (taskId.startsWith('custom')) {
        // Pour les tâches custom, on pourrait aussi enregistrer l'heure
        // Mais pour l'instant on notifie juste
      }

      // Afficher encouragement
      setCurrentEncouragement({
        message: `🚀 C'est parti pour "${task.task}" !`,
        type: 'success'
      });
      setTimeout(() => setCurrentEncouragement(null), 3000);
    }

    // Fermer le modal
    setTaskToStart(null);
  }, [currentSchedule, agendaEvents]);

  // Handler pour reporter une tâche (snooze)
  const handleTaskSnooze = useCallback((taskId, minutes) => {
    // Utiliser le snooze du système de notifications
    notifications.snoozeTask(taskId, currentSchedule.flatMap(tg => tg.tasks).find(t => t.id === taskId)?.category);

    setCurrentEncouragement({
      message: `⏰ Rappel dans ${minutes} minutes`,
      type: 'info'
    });
    setTimeout(() => setCurrentEncouragement(null), 2000);

    // Fermer le modal et reset pour permettre re-notification
    setTaskToStart(null);
    // Ne pas reset lastNotifiedTaskId pour permettre une re-notification après snooze
  }, [notifications, currentSchedule]);

  // Sauvegarder une nouvelle tâche (+ bridge vers agendaEvents pour vue semaine)
  const saveNewTask = async () => {
    if (!newTask.task.trim()) return;
    const dayDateStr = formatDateKey(selectedDate);

    const taskData = {
      ...newTask,
      date: dayDateStr,
      points: 10,
      iconName: 'ListTodo',
      instructions: newTask.instructions.filter(i => i.trim() !== "")
    };

    addTask(taskData);
    bridgeTaskToAgendaEvent(taskData);

    setIsAddingTask(false);
    setNewTask({ task: "", time: "14:00", duration: 60, category: "chore", desc: "", instructions: [""] });
  };

  // Supprimer une tâche
  const handleDeleteTask = (e, taskId) => {
    e.stopPropagation();
    removeTask(taskId);
  };

  // Compléter une tâche — délègue la logique à useTaskManager, gère les effets UI ici
  const handleCompleteTask = useCallback((item, isSubtask = false, subIndex = null) => {
    const result = completeTaskAction(item, isSubtask, subIndex);

    if (result.action === 'missing-subtasks') {
      setCurrentEncouragement({
        message: `⚠️ Complete toutes les étapes d'abord ! (${result.completed}/${result.total})`,
        type: 'warning'
      });
      setTimeout(() => setCurrentEncouragement(null), 3000);
      return;
    }

    if (result.action === 'uncompleted') {
      // Sync agenda event if needed
      if (item.isAgendaEvent) {
        agendaEvents.updateEvent(item.id, { completed: false });
      }
      return;
    }

    if (result.action === 'needs-proof') {
      setPendingTaskCompletion(item);
      setShowProofCapture(item);
    }
    // 'subtask-completed' / 'subtask-toggled' → no UI side effect needed
  }, [completeTaskAction, agendaEvents]);

  // Upload preuve vers Google (Drive + Photos) - défini avant handleProofCapture
  const handleUploadProofToGoogle = useCallback(async (imageData, taskId, taskInfo) => {
    if (!google.isSignedIn) {
      return;
    }

    // Afficher le toast "en cours"
    setUploadStatus({
      status: 'uploading',
      fileName: taskInfo.task || 'Preuve',
      folderPath: `${taskInfo.category || 'chore'} / ${taskInfo.date || 'Aujourd\'hui'}`
    });

    try {
      const results = await google.uploadProofPhoto(imageData, taskId, taskInfo);

      if (results.drive) {
        // Succès Drive
        setUploadStatus({
          status: 'success',
          fileName: results.drive.fileName || taskInfo.task,
          webViewLink: results.drive.webViewLink,
          folderPath: `${results.drive.category || taskInfo.category} / ${results.drive.date || taskInfo.date}`
        });
        console.log('✅ Preuve uploadée sur Google Drive:', results.drive);
      } else if (results.photos) {
        // Succès Photos seulement
        setUploadStatus({
          status: 'success',
          fileName: taskInfo.task,
          webViewLink: results.photos.webViewLink,
          folderPath: 'Google Photos'
        });
      } else {
        // Aucun upload réussi
        setUploadStatus({
          status: 'error',
          error: 'Drive et Photos non disponibles'
        });
        captureError(new Error('Drive et Photos non disponibles'), 'upload proof', SEVERITY.BACKGROUND);
      }
    } catch (error) {
      captureError(error, 'upload proof', SEVERITY.USER_FACING);
      setUploadStatus({
        status: 'error',
        error: error.message || 'Erreur inconnue'
      });
    }
  }, [google]);

  // Gérer l'ajout de preuve (local + Google) et compléter la tâche
  const handleProofCapture = useCallback(async (proofData) => {
    // Sauvegarder localement
    await addProof(proofData);
    gameification.addProof();

    // Uploader vers Google Drive automatiquement avec organisation par dossiers
    if (google.isSignedIn && proofData.imageData) {
      handleUploadProofToGoogle(
        proofData.imageData,
        proofData.taskId,
        {
          category: proofData.category || 'chore',
          task: proofData.taskName || proofData.taskId,
          date: proofData.date || new Date().toISOString().split('T')[0]
        }
      );
    }

    // Compléter la tâche en attente si présente
    if (pendingTaskCompletion) {
      const item = pendingTaskCompletion;

      // Animation
      setCompletingTaskId(item.id);
      setTimeout(() => setCompletingTaskId(null), 600);

      // Marquer complétée + XP + coins + streak via useTaskManager
      finalizeCompletion(item);

      // Sync agenda event dans Firestore
      if (item.isAgendaEvent) {
        agendaEvents.updateEvent(item.id, { completed: true });
      }

      // Annuler les notifications pour cette tâche
      notifications.cancelNotifications(item.id);

      // Ajouter la tâche complétée à Google Tasks
      if (google.isSignedIn && google.servicesStatus?.tasks) {
        const completedDate = new Date().toLocaleDateString('fr-FR');
        google.addNote(
          `✅ ${item.task}`,
          `Complétée le ${completedDate}\nCatégorie: ${item.category}\nDurée: ${item.duration || 30} min${proofData.imageData ? '\n📷 Preuve photo ajoutée' : ''}`,
          null
        ).catch(err => captureError(err, 'ajout Google Tasks', SEVERITY.BACKGROUND));
      }

      // Confetti pour tâches vitales
      if (item.category === 'vital') {
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);
      }

      const categoryMsg = item.category === 'vital' ? '🏆 Tâche vitale' : '✅ Tâche';
      setCurrentEncouragement({
        message: google.isSignedIn
          ? `${categoryMsg} complétée + sync Google ! 📸☁️`
          : `${categoryMsg} complétée + preuve ajoutée ! 📸`,
        type: item.category === 'vital' ? 'vital' : 'success'
      });

      setPendingTaskCompletion(null);
    } else {
      // Preuve ajoutée manuellement (pas de tâche en attente)
      setCurrentEncouragement({
        message: google.isSignedIn ? "Preuve ajoutée et synchronisée ! +5 XP 📸☁️" : "Preuve ajoutée ! +5 XP 📸",
        type: 'success'
      });
    }

    setTimeout(() => setCurrentEncouragement(null), 3000);
  }, [addProof, gameification, google, handleUploadProofToGoogle, pendingTaskCompletion, finalizeCompletion, agendaEvents, notifications]);

  // Démarrer le mode focus
  const handleStartFocus = useCallback((task, preset) => {
    focusMode.startFocus(task, preset);
    setShowFocusStart(null);
    gameification.startTask(task); // XP de démarrage
  }, [focusMode, gameification]);

  // Terminer session focus
  const handleCompleteFocus = useCallback(() => {
    gameification.completeFocusSession();
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 2000);
  }, [gameification]);

  // Handler pour le wizard de configuration
  const handleSetupComplete = useCallback((weekTasks, scheduleSettings) => {
    // Ajouter les tâches générées
    Object.entries(weekTasks).forEach(([dateStr, tasks]) => {
      tasks.forEach(task => {
        addTask({
          ...task,
          date: dateStr,
          isGenerated: true
        });
      });
    });

    setShowSetupWizard(false);
    setCurrentEncouragement({
      message: '🎉 Ton planning est prêt ! Bonne semaine !',
      type: 'success'
    });
    setTimeout(() => setCurrentEncouragement(null), 4000);
  }, [addTask]);

  // Handler pour appliquer un template personnalisé
  const handleApplyTemplate = useCallback((templateId) => {
    const weekTasks = customTemplates.applyTemplate(templateId, new Date());
    if (weekTasks) {
      Object.entries(weekTasks).forEach(([dateStr, tasks]) => {
        tasks.forEach(task => {
          addTask({
            ...task,
            date: dateStr
          });
        });
      });
      setCurrentEncouragement({
        message: '📋 Template appliqué avec succès !',
        type: 'success'
      });
      setTimeout(() => setCurrentEncouragement(null), 3000);
    }
  }, [customTemplates, addTask]);

  // Handler pour confirmer la génération automatique
  const handleConfirmAutoGeneration = useCallback(() => {
    const weekTasks = autoGeneration.confirmGeneration();
    if (weekTasks) {
      Object.entries(weekTasks).forEach(([dateStr, tasks]) => {
        tasks.forEach(task => {
          addTask({
            ...task,
            date: dateStr,
            isGenerated: true
          });
        });
      });
      setCurrentEncouragement({
        message: '🎉 Ta semaine a été générée !',
        type: 'success'
      });
      setTimeout(() => setCurrentEncouragement(null), 4000);
    }
  }, [autoGeneration, addTask]);

  // Connexion Google (via hook centralisé)
  const handleGoogleSignIn = async () => {
    try {
      await google.signIn();
    } catch (error) {
      captureError(error, 'Google sign-in (main)', SEVERITY.USER_FACING);
      // Afficher l'erreur réelle pour le debug
      const errorMessage = error.message || error.code || 'Erreur inconnue';
      if (errorMessage.includes('annulée') || errorMessage.includes('cancelled')) {
        // L'utilisateur a annulé, pas besoin d'alerte
        return;
      }
      alert(`Erreur de connexion: ${errorMessage}`);
    }
  };

  // Synchroniser les tâches vers Google Drive
  const handleSyncTasks = useCallback(async () => {
    if (google.isSignedIn && google.servicesStatus.drive) {
      const allTasks = customTasks;
      const result = await google.syncTasks(allTasks);
      if (result) {
        setCurrentEncouragement({
          message: "Tâches synchronisées sur Drive! ☁️",
          type: 'success'
        });
        setTimeout(() => setCurrentEncouragement(null), 3000);
      }
    }
  }, [google, customTasks]);

  // Export CSV de la semaine
  const exportWeekToCSV = useCallback(() => {
    const categoryLabels = {
      vital: 'Vital (rouge)',
      chore: 'Corvée (orange)',
      pleasure: 'Plaisir (vert)',
      reward: 'Récompense (violet)'
    };

    const categoryColors = {
      vital: '#dc2626',
      chore: '#d97706',
      pleasure: '#059669',
      reward: '#7c3aed'
    };

    // En-têtes du CSV
    const headers = [
      'Jour',
      'Date',
      'Type de jour',
      'Férié',
      'Heure',
      'Tâche',
      'Catégorie',
      'Code couleur',
      'Durée (min)',
      'Description',
      'Sous-tâches',
      'Points',
      'Complétée'
    ];

    const rows = [];

    // Parcourir chaque jour de la semaine
    weekDates.forEach((date, dayIndex) => {
      const dateStr = formatDateKey(date);
      const dayName = daysNames[dayIndex];
      const formattedDate = `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
      const holiday = checkFrenchHoliday(date);
      const isWeekendDay = dayIndex >= 5;
      const dayType = holiday ? 'Férié' : isWeekendDay ? 'Week-end' : 'Jour ouvrable';
      const holidayName = holiday ? holiday.name : '';

      // Récupérer les tâches personnalisées pour ce jour
      const dayCustomTasks = getTasksForDate(dateStr);

      // Générer le planning du jour
      const daySchedule = generateDailySchedule(dayIndex, {
        holiday,
        customTasks: dayCustomTasks
      });

      // Les événements Google sont maintenant inclus dans daySchedule via dayCustomTasks
      const allTasks = daySchedule.sort((a, b) => a.time.localeCompare(b.time));

      if (allTasks.length === 0) {
        // Ajouter une ligne même s'il n'y a pas de tâches
        rows.push([
          dayName,
          formattedDate,
          dayType,
          holidayName,
          '',
          'Aucune tâche',
          '',
          '',
          '',
          '',
          '',
          '',
          ''
        ]);
      } else {
        allTasks.forEach(task => {
          const category = task.category || 'default';
          const categoryLabel = categoryLabels[category] || category;
          const colorCode = categoryColors[category] || '#64748b';
          const subTasks = task.instructions ? task.instructions.join(' | ') : '';
          const isTaskCompleted = isCompleted(weekOffset, dayIndex, task.id);
          const completedStatus = isTaskCompleted ? 'Oui' : 'Non';

          rows.push([
            dayName,
            formattedDate,
            dayType,
            holidayName,
            task.time,
            task.task,
            categoryLabel,
            colorCode,
            task.duration || 30,
            task.desc || '',
            subTasks,
            task.points || 0,
            completedStatus
          ]);
        });
      }
    });

    // Générer le contenu CSV avec tabulations
    const csvContent = [
      headers.join('\t'),
      ...rows.map(row => row.map(cell => {
        // Échapper les tabulations et retours à la ligne dans les cellules
        const cellStr = String(cell).replace(/\t/g, ' ').replace(/\n/g, ' ');
        return cellStr;
      }).join('\t'))
    ].join('\n');

    // Ajouter BOM pour UTF-8 (compatibilité Excel)
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Créer le lien de téléchargement
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const startDate = weekDates[0];
    const endDate = weekDates[6];
    const fileName = `planning_semaine_${startDate.getDate()}-${startDate.getMonth() + 1}_au_${endDate.getDate()}-${endDate.getMonth() + 1}_${startDate.getFullYear()}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [weekDates, getTasksForDate, isCompleted, weekOffset]);

  // Écran de chargement pendant l'initialisation Google (sauf mode local)
  if (!isLocalMode && google.isInitializing) {
    return <GoogleLoadingScreen />;
  }

  // Écran d'onboarding si non connecté à Google et pas en mode local
  if (!isLocalMode && !google.isSignedIn) {
    return (
      <GoogleOnboarding
        onSignIn={handleGoogleSignIn}
        onSkipGoogle={onEnableLocalMode}
        isLoading={false}
        error={google.error}
      />
    );
  }

  if (!selectedDate) {
    return (
      <div className="min-h-screen bg-[#08080c] flex items-center justify-center">
        <div className="text-zinc-400">Chargement...</div>
      </div>
    );
  }

  // Valeur partagée via AppContext — états consommés par 3+ composants sans prop drilling
  const appContextValue = {
    selectedDate,
    currentView: activeView,
    setCurrentView: setActiveViewPersisted,
    showCrisisMode,
    setShowCrisisMode,
    gamificationStats: gameification.stats,
    level: gameification.level,
    currentStreak: gameification.currentStreak,
    currentMood: mood.currentMood,
    currentEnergy: mood.currentEnergy,
  };

  return (
    <AppProvider value={appContextValue}>
    <div className="min-h-screen bg-[var(--theme-bg-base,#08080c)] font-sans text-zinc-100 select-none">
      {/* Top Status Bar - Fixed */}
      <TopStatusBar
        activeTask={activeTaskId ? currentSchedule.flatMap(g => g.tasks).find(t => t.id === activeTaskId) : null}
        nextTask={nextUpcomingTask}
        timeLeft={timeLeft}
        progressPercent={progressPercent}
        dailyProgress={dailyProgress}
        servicesStatus={servicesStatus.statuses}
        onStatusClick={() => servicesStatus.checkAllServices()}
        onCrisisClick={() => setShowCrisisMode(true)}
        onReconnect={google.reconnect}
        conflictCount={getConflictCount()}
        onConflictsClick={() => setShowConflictSheet(true)}
        pendingSyncOps={pendingSyncOps}
        isSyncing={isSyncingCalendar || google.isSyncing || agendaEvents.isSyncing}
        notifHistory={notifications.notifHistory}
        unreadNotifCount={notifications.unreadCount}
        onMarkAllRead={notifications.markAllRead}
        onMarkRead={notifications.markRead}
        onClearNotifHistory={notifications.clearHistory}
      />

      {/* Main Container */}
      <div
        className={`${activeView === 'weekly' ? '' : 'max-w-md md:max-w-lg lg:max-w-2xl'} mx-auto min-h-screen bg-[var(--theme-bg-surface,#0e0e14)] shadow-2xl shadow-black/60`}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >

        {/* ════ DASHBOARD VIEW ════ */}
        <div style={{ position: 'fixed', top: 52, bottom: 80, left: 0, right: 0, display: activeView === 'dashboard' ? 'flex' : 'none', flexDirection: 'column' }}>
          <DashboardPage
              tasks={customTasks}
              isTaskCompleted={isTaskCompleted}
              isSubTaskCompleted={isSubTaskCompleted}
              toggleSubTask={toggleSubTask}
              getCompletedCount={getCompletedCount}
              weekOffset={weekOffset}
              currentDayIndex={currentDayIndex}
              gameification={gameification}
              mood={mood}
              weather={weatherData}
              calendarSynced={google.isSignedIn && google.servicesStatus?.calendar}
              isSyncingCalendar={isSyncingCalendar || google.isSyncing}
              onAddTask={() => setIsAddingTask(true)}
              onStartFocus={(task) => {
                if (task) {
                  focusMode.startSession(task);
                }
              }}
              onOpenTaskDetail={(task) => setSelectedTaskDetail(task)}
              onGoToDay={() => setActiveView('daily')}
              onCompleteTask={(task) => {
                const action = completeTaskAction(currentDayIndex, task);
                if (action) finalizeCompletion(action, task, currentDayIndex);
              }}
              onCreateTask={(taskData) => {
                const fullTask = { ...taskData, points: 10 };
                addTask(fullTask);
                bridgeTaskToAgendaEvent(fullTask);
                setCurrentEncouragement({
                  message: `✨ Tâche "${taskData.task}" créée par l'IA !`,
                  type: 'success'
                });
                setTimeout(() => setCurrentEncouragement(null), 3000);
              }}
            />
        </div>

        {/* Block Notes View */}
        <div style={{ display: activeView === 'notes' ? 'block' : 'none' }}>
          <BlockNotesView />
        </div>

        {/* Pricing View */}
        {activeView === 'pricing' && (
          <PricingPage
            userId={google?.user?.uid}
            userEmail={google?.user?.email}
            isPro={subscription.isPro}
            onClose={() => setActiveViewPersisted('dashboard')}
          />
        )}

        {/* ════ WEEKLY VIEW ════ */}
        <div className="pt-[60px] pb-[72px] h-screen" style={{ display: activeView === 'weekly' ? 'block' : 'none' }}>
            <WeeklyAgenda
              events={mergedWeeklyEvents}
              onEventsChange={handleEventsChange}
              onDeleteEvent={handleDeleteEvent}
              isSyncing={agendaEvents.isSyncing}
              syncError={agendaEvents.syncError}
              isOnline={agendaEvents.isOnline}
              onEventClick={(event) => {
                setEditingAgendaEvent(event);
                setShowEventEditor(true);
              }}
              onSetupRequest={() => {
                // Start week setup - could open a wizard here
                agendaEvents.markSetupDone();
              }}
              onSetupSkip={() => {
                // User chose "Plus tard"
                agendaEvents.markSetupDone();
              }}
              showSetupPrompt={!agendaEvents.hasSetupAgenda && agendaEvents.events.length === 0}
              gameification={gameification}
              mood={mood}
              onEventComplete={(event) => {
                // Marquer l'événement comme complété
                agendaEvents.updateEvent(event.id, { completed: !event.completed });

                // Syncer la complétion vers le customTask lié
                const linkedTask = (customTasks || []).find(t =>
                  t.linkedEventId === event.id || t.id === event.linkedTaskId
                );
                if (linkedTask && !event.completed) {
                  // Compléter le task via le système normal
                  handleCompleteTask(linkedTask);
                }

                // Donner XP et coins si on complète (pas si on décoche)
                if (!event.completed) {
                  gameification.completeTask({ category: event.category || 'chore' }, false);
                  rewards.addCoins(event.category === 'vital' ? 15 : 10, event.category || 'event');
                  setShowConfetti(true);
                  setCurrentEncouragement({
                    message: `${event.title} terminé ! +XP +Coins`,
                    type: 'success'
                  });
                }
              }}
              onCelebrate={(message, type = 'success', showConfettiAnim = true) => {
                // Callback pour déclencher la célébration depuis WeeklyAgenda
                if (showConfettiAnim) setShowConfetti(true);
                setCurrentEncouragement({ message, type });
              }}
            />
        </div>

        {/* ════ DAILY VIEW ════ */}
        <div className="pt-[52px] pb-[88px] min-h-screen" style={{ display: activeView === 'daily' ? 'block' : 'none' }}>

          {/* ── En-tête compact du jour ── */}
          <div className="px-3 py-2">
            <DayHeader
              dayName={daysNames[currentDayIndex]}
              dayNumber={selectedDate.getDate()}
              holiday={holiday}
              isWeekend={isWeekend}
              isNonWorkable={isNonWorkable}
              mood={mood}
              onMoodClick={() => mood.setShowCheckIn(true)}
              weather={weatherData.weather}
              isWeatherLoading={weatherData.isLoading}
              streak={gameification.currentStreak || 0}
              level={gameification.level || 1}
              dailyProgress={dailyProgress.percent || 0}
            />
          </div>

          {/* Timeline verticale des tâches - 24h */}
          <div className="relative overflow-visible">
            {/* Ligne verticale */}
            <div className="absolute top-0 bottom-0 rounded-full" style={{ left: '59px', width: '2px', background: 'linear-gradient(to bottom, rgba(63,63,70,0.3), rgba(113,113,122,0.6) 30%, rgba(113,113,122,0.6) 70%, rgba(63,63,70,0.3))' }} />

            {/* Indicateur de l'heure actuelle - trait horizontal rouge */}
            {weekOffset === 0 && (() => {
              const currentHour = currentTime.getHours();
              const currentMinutes = currentTime.getMinutes();
              const topPosition = (currentHour * 60 + currentMinutes) / 60 * 88;
              return (
                <div
                  className="absolute left-0 right-0 z-10 pointer-events-none"
                  style={{ top: `${topPosition}px` }}
                >
                  {/* Petit point discret */}
                  <div className="absolute rounded-full" style={{ left: '56px', top: '-3px', width: '6px', height: '6px', background: 'rgba(255,255,255,0.28)' }} />
                  {/* Trait fin semi-transparent */}
                  <div className="absolute right-0" style={{ left: '62px', height: '1px', background: 'linear-gradient(to right, rgba(255,255,255,0.22), rgba(255,255,255,0.06) 55%, transparent)' }} />
                </div>
              );
            })()}

            {/* Timeline 24h */}
            {(() => {
              const HOUR_HEIGHT = 88;
              const TIMELINE_LEFT = 68;
              const allTasks = currentSchedule.flatMap(tg => tg.tasks);
              const now = new Date();
              const currentHour = now.getHours();

              // === Calcul des superpositions pour affichage côte-à-côte ===
              const timeToMinutes = (time) => {
                const [h, m] = time.split(':').map(Number);
                return h * 60 + m;
              };

              // Trier par heure de début
              const sortedTasks = [...allTasks].sort((a, b) =>
                timeToMinutes(a.time) - timeToMinutes(b.time)
              );

              // Algorithme deux passes pour éviter références circulaires
              // Pass 1: Identifier les groupes qui se chevauchent
              const taskColumns = new Map(); // id -> column
              const taskGroups = new Map(); // id -> groupSize

              sortedTasks.forEach((task, idx) => {
                const startMins = timeToMinutes(task.time);
                const endMins = startMins + (task.duration || 30);

                // Trouver les tâches précédentes qui chevauchent celle-ci
                const occupiedColumns = new Set();
                let maxGroupSize = 1;

                for (let i = 0; i < idx; i++) {
                  const other = sortedTasks[i];
                  const otherStart = timeToMinutes(other.time);
                  const otherEnd = otherStart + (other.duration || 30);

                  if (startMins < otherEnd && endMins > otherStart) {
                    // Chevauchement détecté
                    const col = taskColumns.get(other.id) || 0;
                    occupiedColumns.add(col);
                    maxGroupSize = Math.max(maxGroupSize, (taskGroups.get(other.id) || 1) + 1);
                  }
                }

                // Assigner la première colonne disponible
                let column = 0;
                while (occupiedColumns.has(column)) column++;
                taskColumns.set(task.id, column);
                taskGroups.set(task.id, maxGroupSize);
              });

              // Pass 2: Recalculer la taille de groupe réelle pour chaque tâche
              const tasksWithLayout = sortedTasks.map((task) => {
                const startMins = timeToMinutes(task.time);
                const endMins = startMins + (task.duration || 30);

                // Compter le nombre total de colonnes dans ce groupe de superposition
                let totalColumns = 1;
                sortedTasks.forEach((other) => {
                  if (other.id === task.id) return;
                  const otherStart = timeToMinutes(other.time);
                  const otherEnd = otherStart + (other.duration || 30);
                  if (startMins < otherEnd && endMins > otherStart) {
                    totalColumns = Math.max(totalColumns, (taskColumns.get(other.id) || 0) + 1, (taskColumns.get(task.id) || 0) + 1);
                  }
                });

                return {
                  ...task,
                  column: taskColumns.get(task.id) || 0,
                  totalColumns,
                  hasOverlap: totalColumns > 1
                };
              });

              // Zones du jour
              const getZoneBg = (h) => {
                if (h < 6)  return 'rgba(23,37,84,0.14)';
                if (h < 12) return 'rgba(234,179,8,0.03)';
                if (h < 18) return 'rgba(59,130,246,0.03)';
                return 'rgba(139,92,246,0.05)';
              };
              const zoneLabels = [
                { hour: 0, label: 'Nuit', icon: '🌙' },
                { hour: 6, label: 'Matin', icon: '🌅' },
                { hour: 12, label: 'Après-midi', icon: '☀️' },
                { hour: 18, label: 'Soir', icon: '🌆' },
              ];

              return (
                <div className="relative" style={{ height: `${24 * HOUR_HEIGHT}px` }}>

                  {/* Zones de la journée */}
                  {[[0,6],[6,12],[12,18],[18,24]].map(([start, end]) => (
                    <div
                      key={start}
                      className="absolute left-0 right-0 pointer-events-none"
                      style={{ top: `${start * HOUR_HEIGHT}px`, height: `${(end - start) * HOUR_HEIGHT}px`, background: getZoneBg(start) }}
                    />
                  ))}

                  {/* Labels de zone */}
                  {zoneLabels.map(({ hour, label, icon }) => (
                    <div
                      key={hour}
                      className="absolute pointer-events-none flex items-center gap-1 opacity-30"
                      style={{ top: `${hour * HOUR_HEIGHT + 6}px`, right: '8px' }}
                    >
                      <span className="text-[9px]">{icon}</span>
                      <span className="text-[9px] font-medium uppercase tracking-widest text-zinc-400">{label}</span>
                    </div>
                  ))}

                  {/* Grille horaire */}
                  {Array.from({ length: 24 }, (_, hour) => {
                    const hourStr = hour.toString().padStart(2, '0');
                    const isCurrentHour = weekOffset === 0 && hour === currentHour;
                    const isPastHour = weekOffset === 0 && hour < currentHour;
                    const hasTasksAtHour = allTasks.some(t => t.time.startsWith(hourStr));
                    const showLabel = hasTasksAtHour || hour % 3 === 0;

                    return (
                      <div
                        key={hour}
                        className="absolute left-0 right-0"
                        style={{ top: `${hour * HOUR_HEIGHT}px`, height: `${HOUR_HEIGHT}px` }}
                      >
                        {/* Ligne principale */}
                        <div
                          className="absolute right-0"
                          style={{
                            left: `${TIMELINE_LEFT - 8}px`,
                            top: 0,
                            height: isCurrentHour ? '1.5px' : '1px',
                            background: isCurrentHour
                              ? 'rgba(239,68,68,0.2)'
                              : hasTasksAtHour
                                ? 'rgba(161,161,170,0.13)'
                                : 'rgba(63,63,70,0.18)',
                          }}
                        />
                        {/* Ligne demi-heure */}
                        <div
                          className="absolute right-0 pointer-events-none"
                          style={{
                            left: `${TIMELINE_LEFT}px`,
                            top: `${HOUR_HEIGHT / 2}px`,
                            height: '1px',
                            background: 'rgba(63,63,70,0.10)',
                            borderTop: '1px dashed rgba(63,63,70,0.12)',
                          }}
                        />
                        {/* Label heure */}
                        {showLabel && (
                          <div
                            className="absolute top-0 flex items-center justify-end"
                            style={{ left: 0, width: `${TIMELINE_LEFT - 10}px`, paddingRight: '6px' }}
                          >
                            <span className={`text-[12px] font-semibold tabular-nums leading-none pt-0.5 ${
                              isCurrentHour ? 'text-red-400 font-bold' :
                              isPastHour    ? 'text-zinc-700' :
                              hasTasksAtHour ? 'text-zinc-400' :
                              'text-zinc-700'
                            }`}>
                              {hourStr}<span className="text-[9px] opacity-60">h</span>
                            </span>
                          </div>
                        )}
                        {/* Point sur la ligne verticale */}
                        {hasTasksAtHour && (
                          <div
                            className="absolute z-10 rounded-full"
                            style={{
                              left: `${TIMELINE_LEFT - 11}px`,
                              top: '-3px',
                              width: isCurrentHour ? '12px' : '8px',
                              height: isCurrentHour ? '12px' : '8px',
                              marginTop: isCurrentHour ? '-3px' : 0,
                              background: isCurrentHour ? '#ef4444' : isPastHour ? '#3f3f46' : '#71717a',
                              boxShadow: isCurrentHour ? '0 0 8px rgba(239,68,68,0.5)' : undefined,
                            }}
                          />
                        )}
                      </div>
                    );
                  })}

                  {/* Cartes de tâches */}
                  {tasksWithLayout.map((item) => {
                    const [taskHour, taskMinute] = item.time.split(':').map(Number);
                    const startMinutes = taskHour * 60 + taskMinute;
                    const topPosition = (startMinutes / 60) * HOUR_HEIGHT;
                    const durationMinutes = item.duration || 30;
                    const taskHeight = Math.max(52, (durationMinutes / 60) * HOUR_HEIGHT);

                    const completed = isTaskCompleted(currentDayIndex, item);
                    const isActive = weekOffset === 0 && activeTaskId === item.id;
                    const isDeveloped = isActive && !completed;
                    const styles = getCategoryStyles(item.category, settings.safetyMode);
                    const hasInstructions = item.instructions && item.instructions.length > 0;
                    const isImported = item.isImported;
                    const taskProofs = getProofsForTask(item.id);
                    if (styles === 'hidden') return null;

                    const getExpandedStyles = (cat) => {
                      switch (cat) {
                        case 'vital': return 'task-vital-expanded';
                        case 'chore': return 'task-chore-expanded';
                        case 'pleasure': return 'task-pleasure-expanded';
                        case 'reward': return 'task-reward-expanded';
                        default: return 'task-default-expanded';
                      }
                    };
                    const isCompleting = completingTaskId === item.id;
                    const getCatColor = (cat) => {
                      switch (cat) {
                        case 'vital': return '#ef4444';
                        case 'chore': return '#f59e0b';
                        case 'pleasure': return '#22c55e';
                        case 'reward': return '#8b5cf6';
                        default: return '#71717a';
                      }
                    };
                    const getCatIcon = (cat) => {
                      switch (cat) {
                        case 'vital': return '❤️';
                        case 'chore': return '⚡';
                        case 'pleasure': return '🌿';
                        case 'reward': return '✨';
                        default: return '•';
                      }
                    };
                    const elapsedPercent = isActive && !completed ? progressPercent : 0;
                    const categoryColor = getCatColor(item.category);

                    // Heure de fin
                    let endTime;
                    if (item.endTime) { endTime = item.endTime; }
                    else {
                      const totalM = startMinutes + durationMinutes;
                      endTime = `${String(Math.floor(totalM/60)%24).padStart(2,'0')}:${String(totalM%60).padStart(2,'0')}`;
                    }

                    // Position horizontale
                    const GAP = 4;
                    const columnWidth = item.hasOverlap
                      ? `calc((100% - ${TIMELINE_LEFT + GAP}px) / ${item.totalColumns} - ${GAP}px)`
                      : `calc(100% - ${TIMELINE_LEFT + GAP}px)`;
                    const leftOffset = item.hasOverlap
                      ? `calc(${TIMELINE_LEFT + GAP}px + ${item.column} * ((100% - ${TIMELINE_LEFT + GAP}px) / ${item.totalColumns}))`
                      : `${TIMELINE_LEFT + GAP}px`;

                    return (
                      <div
                        key={item.id}
                        className={`absolute rounded-xl overflow-hidden cursor-pointer transition-all duration-200 ${isCompleting ? 'completing' : ''} ${
                          isActive ? `${getExpandedStyles(item.category)} ring-1 ring-white/10` : ''
                        }`}
                        style={{
                          left: leftOffset,
                          width: columnWidth,
                          top: `${topPosition + 2}px`,
                          height: isActive ? 'auto' : `${taskHeight - 4}px`,
                          minHeight: `${taskHeight - 4}px`,
                          borderLeft: `4px solid ${categoryColor}`,
                          background: completed
                            ? 'rgba(39,39,42,0.22)'
                            : isActive
                              ? `linear-gradient(145deg, ${categoryColor}28, ${categoryColor}0c)`
                              : `linear-gradient(145deg, ${categoryColor}18, ${categoryColor}08)`,
                          boxShadow: completed ? 'none' : isActive
                            ? `0 6px 20px ${categoryColor}28, inset 0 1px 0 rgba(255,255,255,0.06)`
                            : `0 2px 6px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.03)`,
                          opacity: completed ? 0.42 : 1,
                          zIndex: isActive ? 20 : 10,
                        }}
                        onClick={() => setSelectedTaskDetail({ ...item, isCompleted: completed })}
                      >
                        {/* Halo de progression */}
                        {isActive && !completed && elapsedPercent > 0 && (
                          <div
                            className="absolute inset-0 pointer-events-none transition-all duration-1000 rounded-xl"
                            style={{ background: `linear-gradient(to right, ${categoryColor}1a ${elapsedPercent}%, transparent ${elapsedPercent}%)` }}
                          />
                        )}

                        {/* Contenu */}
                        <div className={`relative flex flex-col h-full ${taskHeight < 62 ? 'px-2 py-1.5' : 'px-3 py-2'}`}>

                          {/* Ligne 1 : icône + titre + actions */}
                          <div className="flex items-start gap-1.5 min-w-0">
                            <span className="text-[12px] leading-none mt-0.5 flex-shrink-0 select-none">
                              {getCatIcon(item.category)}
                            </span>
                            <h3 className={`font-semibold leading-snug flex-1 min-w-0 ${
                              taskHeight < 62 ? 'text-[12px]' : 'text-[14px]'
                            } ${
                              completed ? 'line-through text-zinc-500' : 'text-zinc-100'
                            } ${isDeveloped ? '' : 'truncate'}`}>
                              {item.task}
                            </h3>
                            <div className="flex items-center gap-0.5 flex-shrink-0">
                              {isImported && (
                                <span className="bg-blue-500/20 text-blue-400 text-[8px] font-bold px-1 rounded-sm">G</span>
                              )}
                              {hasInstructions && !isDeveloped && (
                                <span className="bg-zinc-700/60 text-zinc-400 text-[9px] font-medium px-1.5 rounded-full">
                                  {getCompletedCount(weekOffset, currentDayIndex, item.id, item.instructions.length)}/{item.instructions.length}
                                </span>
                              )}
                              {item.isAgendaEvent && (
                                <button onClick={(e) => { e.stopPropagation(); setActiveView('weekly'); }}
                                  className="p-0.5 text-cyan-500/60 hover:text-cyan-400 transition-colors">
                                  <Calendar className="w-3 h-3"/>
                                </button>
                              )}
                              {item.id.startsWith('custom') && (
                                <button onClick={(e) => handleDeleteTask(e, item.id)}
                                  className="p-0.5 text-zinc-700 hover:text-red-400 transition-colors">
                                  <Trash2 className="w-3 h-3"/>
                                </button>
                              )}
                            </div>
                          </div>

                          {/* Ligne 2 : plage horaire + durée */}
                          {taskHeight >= 58 && (
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`text-[11px] font-mono tabular-nums ${
                                isActive && !completed ? 'text-zinc-200' : 'text-zinc-500'
                              }`}>
                                {item.time} – {endTime}
                              </span>
                              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                                style={{ background: `${categoryColor}22`, color: categoryColor }}>
                                {formatDuration(durationMinutes)}
                              </span>
                              {item.desc && (
                                <span className={`text-[10px] truncate ${completed ? 'text-zinc-600' : 'text-zinc-500'}`}>
                                  {item.desc}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Sous-tâches IA */}
                          {isDeveloped && !completed && (
                            <SubTasksWithAI
                              task={{ ...item, date: formatDateKey(selectedDate) }}
                              instructions={item.instructions || []}
                              weekOffset={weekOffset}
                              currentDayIndex={currentDayIndex}
                              isSubTaskCompleted={isSubTaskCompleted}
                              toggleSubTask={toggleSubTask}
                              getCompletedCount={getCompletedCount}
                              context={{ mood: mood?.mood, energy: mood?.energy }}
                              onAllComplete={() => { setPendingTaskCompletion(item); setShowProofCapture(item); }}
                            />
                          )}
                          {isDeveloped && completed && hasInstructions && (
                            <div className="subtask-area mt-2 space-y-1 p-2 rounded-lg bg-zinc-800/30 opacity-50">
                              <p className="text-[9px] font-medium uppercase flex items-center gap-1 mb-1.5 text-zinc-500">
                                <ListTodo className="w-3 h-3" />
                                {item.instructions.length}/{item.instructions.length}
                              </p>
                              {item.instructions.map((ins, idx) => (
                                <div key={idx} className="flex items-center gap-2 p-1.5 rounded">
                                  <div className="w-4 h-4 rounded border flex items-center justify-center bg-zinc-600 border-zinc-600 text-white">
                                    <CheckCircle2 className="w-3 h-3" />
                                  </div>
                                  <span className="text-[11px] font-medium line-through text-zinc-500">{getInstructionText(ins)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Barre de progression en bas */}
                        {isActive && !completed && (
                          <div className="h-[3px] bg-zinc-800/60 rounded-b-xl overflow-hidden">
                            <div className="h-full transition-all duration-1000 rounded-b-xl"
                              style={{ width: `${progressPercent}%`, background: `linear-gradient(to right, ${categoryColor}, ${categoryColor}cc)` }} />
                          </div>
                        )}

                        {/* Actions rapides */}
                        {!completed && (isDeveloped || isActive) && (
                          <div className="flex items-center gap-1.5 px-2.5 py-2 border-t border-white/[0.06] bg-black/15">
                            <button onClick={(e) => { e.stopPropagation(); setShowFocusStart(item); }}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-zinc-700/60 text-zinc-300 hover:bg-zinc-600/70 transition-colors">
                              <Target className="w-3.5 h-3.5" /> Focus
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); handleStartFocus(item, 'micro'); gameification.startTask(item); }}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-zinc-700/60 text-zinc-300 hover:bg-zinc-600/70 transition-colors">
                              <Zap className="w-3.5 h-3.5" /> 2 min
                            </button>
                            <button onClick={(e) => { e.stopPropagation(); setShowProofCapture(item); }}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-zinc-700/60 text-zinc-300 hover:bg-zinc-600/70 transition-colors">
                              <Camera className="w-3.5 h-3.5" /> Preuve
                            </button>
                            {taskProofs.length > 0 && (
                              <span className="ml-auto text-[10px] font-medium text-zinc-500 flex items-center gap-1">
                                <Camera className="w-3 h-3" /> {taskProofs.length}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            </div>
          </div>

        {/* Bottom Sheet Ajout Tâche */}
        <BottomSheet
          isOpen={isAddingTask}
          onClose={() => setIsAddingTask(false)}
          title="Nouvelle tâche"
        >
          <div className="p-4 space-y-4">
            {/* Presets rapides */}
            <div>
              <label className="text-[10px] text-zinc-500 font-medium mb-2 block uppercase tracking-wide">Tâches rapides</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: 'Méditation', duration: 10, category: 'vital' },
                  { name: 'Sport', duration: 30, category: 'vital' },
                  { name: 'Pause café', duration: 15, category: 'pleasure' },
                  { name: 'Réunion', duration: 60, category: 'chore' },
                ].map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => setNewTask({ ...newTask, task: preset.name, duration: preset.duration, category: preset.category })}
                    className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded-full transition-all active:scale-95"
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </div>

            <input
              type="text"
              value={newTask.task}
              onChange={e => setNewTask({...newTask, task: e.target.value})}
              className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-md p-3.5 text-sm text-white font-medium outline-none focus:border-[var(--theme-accent,#3b82f6)] focus:ring-1 focus:ring-[var(--theme-accent,#3b82f6)] transition-all placeholder:text-zinc-600"
              placeholder="Nom de l'activité"
            />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-500 font-medium mb-1.5 block uppercase tracking-wide">Heure</label>
                <input
                  type="time"
                  value={newTask.time}
                  onChange={e => setNewTask({...newTask, time: e.target.value})}
                  className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-md p-3 text-sm text-white outline-none focus:border-[var(--theme-accent,#3b82f6)] transition-all"
                />
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 font-medium mb-1.5 block uppercase tracking-wide">Durée</label>
                <div className="flex gap-1">
                  {[15, 30, 45, 60].map((d) => (
                    <button
                      key={d}
                      onClick={() => setNewTask({...newTask, duration: d})}
                      className={`flex-1 py-2.5 rounded text-xs font-medium transition-all ${
                        newTask.duration === d
                          ? 'bg-[var(--theme-accent,#3b82f6)] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {d}m
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-[10px] text-zinc-500 font-medium mb-2 block uppercase tracking-wide">Catégorie</label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { id: 'vital', label: 'Vital', color: '#ef4444', emoji: '🔴' },
                  { id: 'chore', label: 'Corvée', color: '#f59e0b', emoji: '🟠' },
                  { id: 'pleasure', label: 'Plaisir', color: '#22c55e', emoji: '🟢' },
                  { id: 'reward', label: 'Récomp.', color: '#8b5cf6', emoji: '🟣' },
                ].map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setNewTask({...newTask, category: cat.id})}
                    className={`flex flex-col items-center justify-center p-2.5 rounded-md transition-all active:scale-95 ${
                      newTask.category === cat.id
                        ? 'ring-2 ring-offset-1 ring-offset-[#0e0e14]'
                        : 'bg-zinc-800/50 hover:bg-zinc-800'
                    }`}
                    style={{
                      backgroundColor: newTask.category === cat.id ? `${cat.color}20` : undefined,
                      ringColor: cat.color,
                    }}
                  >
                    <span className="text-base">{cat.emoji}</span>
                    <span className="text-[9px] text-zinc-400 mt-1">{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[10px] text-zinc-500 font-medium mb-1.5 block uppercase tracking-wide">Sous-tâches (optionnel)</label>
              {newTask.instructions.map((ins, idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={getInstructionText(ins)}
                    onChange={e => {
                      const arr = [...newTask.instructions];
                      arr[idx] = e.target.value;
                      setNewTask({...newTask, instructions: arr});
                    }}
                    className="flex-1 bg-zinc-800/50 border border-zinc-700/50 rounded p-2.5 text-xs text-white outline-none focus:border-zinc-600 placeholder:text-zinc-600"
                    placeholder={`Étape ${idx + 1}`}
                  />
                  {newTask.instructions.length > 1 && (
                    <button onClick={() => setNewTask({...newTask, instructions: newTask.instructions.filter((_, i) => i !== idx)})} className="text-zinc-600 hover:text-zinc-400 transition-colors p-2">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button onClick={() => setNewTask({...newTask, instructions: [...newTask.instructions, ""]})} className="text-zinc-400 text-xs font-medium flex items-center gap-1 mt-2 hover:text-zinc-300 transition-colors">
                <Plus className="w-3 h-3" /> Ajouter une étape
              </button>
            </div>

            <button
              onClick={saveNewTask}
              disabled={!newTask.task.trim()}
              className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-md font-medium text-sm transition-all mt-2 ${
                newTask.task.trim()
                  ? 'bg-[var(--theme-accent,#3b82f6)] text-white hover:brightness-110 active:scale-[0.98]'
                  : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
              }`}
            >
              <Plus className="w-5 h-5" />
              Ajouter la tâche
            </button>
          </div>
        </BottomSheet>

        {/* Bottom Sheet Paramètres - Complet */}
        <BottomSheet
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          title="Paramètres"
          size="large"
          priority="high"
        >
          <div className="p-4 space-y-5 max-h-[70vh] overflow-y-auto">

            {/* Section 1: Bien-être & Suivi */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-emerald-500/10 rounded flex items-center justify-center">
                  <Sun className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <span className="text-xs font-medium text-zinc-300">Bien-être & Suivi</span>
              </div>
              <button
                onClick={() => {
                  setShowSettings(false);
                  mood.setShowCheckIn(true);
                }}
                className="w-full flex items-center gap-3 p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800 rounded-md transition-all group"
              >
                <div className="w-10 h-10 bg-amber-500/10 rounded-md flex items-center justify-center group-hover:bg-amber-500/20 transition-colors">
                  <Sun className="w-5 h-5 text-amber-400" />
                </div>
                <div className="text-left flex-1">
                  <span className="text-sm font-medium text-zinc-200 block">Check-in du jour</span>
                  <span className="text-[11px] text-zinc-500">Comment tu te sens ?</span>
                </div>
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    setShowSettings(false);
                    setShowStats(true);
                  }}
                  className="flex items-center gap-3 p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800 rounded-md transition-all group"
                >
                  <div className="w-9 h-9 bg-purple-500/10 rounded-md flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                    <Trophy className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-medium text-zinc-200 block">Stats</span>
                    <span className="text-[10px] text-zinc-500">Niv. {gameification.level}</span>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setShowSettings(false);
                    setShowVictoryGallery(true);
                  }}
                  className="flex items-center gap-3 p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800 rounded-md transition-all group"
                >
                  <div className="w-9 h-9 bg-blue-500/10 rounded-md flex items-center justify-center group-hover:bg-blue-500/20 transition-colors">
                    <Camera className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="text-left">
                    <span className="text-sm font-medium text-zinc-200 block">Galerie</span>
                    <span className="text-[10px] text-zinc-500">{proofs.length} preuves</span>
                  </div>
                </button>
              </div>
            </div>

            <div className="border-t border-[var(--border-subtle,rgba(255,255,255,0.06))]" />

            {/* Section 2: Sécurité & Urgence */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-red-500/10 rounded flex items-center justify-center">
                  <Shield className="w-3.5 h-3.5 text-red-400" />
                </div>
                <span className="text-xs font-medium text-zinc-300">Sécurité & Urgence</span>
              </div>
              <button
                onClick={() => {
                  setShowSettings(false);
                  setShowCrisisMode(true);
                }}
                className="w-full flex items-center gap-3 p-3 bg-red-950/20 border border-red-900/30 hover:bg-red-950/30 rounded-md transition-all group"
              >
                <div className="w-10 h-10 bg-red-500/20 rounded-md flex items-center justify-center">
                  <Shield className="w-5 h-5 text-red-400" />
                </div>
                <div className="text-left flex-1">
                  <span className="text-sm font-medium text-red-200 block">Mode Crise</span>
                  <span className="text-[11px] text-red-400/70">Respiration, contacts d'aide</span>
                </div>
              </button>
              <SettingToggle
                icon={AlertCircle}
                active={settings.safetyMode}
                label="Mode simplifié"
                description="Affiche uniquement les tâches vitales"
                checked={settings.safetyMode}
                onChange={() => toggleSetting('safetyMode')}
              />
            </div>

            <div className="border-t border-[var(--border-subtle,rgba(255,255,255,0.06))]" />

            {/* Section 3: Notifications */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-zinc-500/10 rounded flex items-center justify-center">
                  <Bell className="w-3.5 h-3.5 text-zinc-400" />
                </div>
                <span className="text-xs font-medium text-zinc-300">Notifications</span>
              </div>
              <SettingToggle
                icon={notifications.enabled ? Bell : BellOff}
                active={notifications.enabled}
                label="Rappels"
                description={notifications.permission === 'granted' ? "Rappels actifs" : "Recevoir des rappels de tâches"}
                checked={notifications.enabled}
                onChange={async () => {
                  if (!notifications.enabled) {
                    const granted = await notifications.requestPermission();
                    if (granted) {
                      notifications.updateSettings({ enabled: true });
                    }
                  } else {
                    notifications.updateSettings({ enabled: false });
                  }
                  toggleSetting('notificationsEnabled');
                }}
              />
            </div>

            <div className="border-t border-[var(--border-subtle,rgba(255,255,255,0.06))]" />

            {/* Section 4: Compte & Données */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-zinc-500/10 rounded flex items-center justify-center">
                  <Cloud className="w-3.5 h-3.5 text-zinc-400" />
                </div>
                <span className="text-xs font-medium text-zinc-300">Compte & Données</span>
              </div>

              {/* Google Account */}
              {isLocalMode ? (
                <div className="flex items-center justify-between p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] rounded-md">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-zinc-700/50 rounded-md flex items-center justify-center">
                      <CloudOff className="w-4 h-4 text-zinc-500" />
                    </div>
                    <div>
                      <span className="text-sm font-medium text-zinc-200 block">Mode Local</span>
                      <span className="text-[11px] text-zinc-500">Données hors-ligne</span>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      onDisableLocalMode();
                      setShowSettings(false);
                    }}
                    className="px-3 py-1.5 bg-zinc-700 hover:bg-zinc-600 text-xs text-zinc-300 font-medium rounded transition-all"
                  >
                    Connecter
                  </button>
                </div>
              ) : (
                <div className="p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] rounded-md space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-md flex items-center justify-center ${google.isSignedIn ? 'bg-emerald-500/10' : 'bg-zinc-700/50'}`}>
                        <Cloud className={`w-4 h-4 ${google.isSignedIn ? 'text-emerald-400' : 'text-zinc-500'}`} />
                      </div>
                      <div>
                        <span className="text-sm font-medium text-zinc-200 block">Google Calendar</span>
                        <span className="text-[11px] text-zinc-500">
                          {google.isSignedIn ? google.user?.email : 'Non connecté'}
                        </span>
                      </div>
                    </div>
                    {google.isSignedIn && (
                      <button
                        onClick={handleSyncTasks}
                        disabled={google.isSyncing}
                        className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700/50 rounded transition-all disabled:opacity-50"
                        title="Synchroniser"
                      >
                        <RefreshCw className={`w-4 h-4 ${google.isSyncing ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </div>

                  {google.isSignedIn ? (
                    <button
                      onClick={google.signOut}
                      className="w-full flex items-center justify-center gap-2 p-2.5 bg-red-950/30 hover:bg-red-900/30 text-red-300 text-sm font-medium rounded transition-all"
                    >
                      <LogOut className="w-4 h-4" /> Déconnexion
                    </button>
                  ) : (
                    <button
                      onClick={handleGoogleSignIn}
                      className="w-full flex items-center justify-center gap-2 bg-white hover:bg-zinc-100 text-zinc-900 rounded p-2.5 text-sm font-medium transition-all"
                    >
                      <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" />
                      Connecter Google
                    </button>
                  )}
                </div>
              )}

              {/* Synchroniser vers Calendar */}
              {!isLocalMode && google.isSignedIn && google.servicesStatus?.calendar && (
                <button
                  onClick={async () => {
                    const today = getWeekDates(weekOffset)[currentDayIndex];
                    const allDayTasks = currentSchedule.flatMap(g => g.tasks);
                    const result = await google.pushToCalendar(allDayTasks, today);
                    setCurrentEncouragement({
                      message: result.pushed > 0
                        ? `📅 ${result.pushed} tâche(s) synchronisée(s) avec Google Calendar !`
                        : result.skipped > 0
                          ? '✓ Tâches déjà synchronisées'
                          : 'Aucune tâche à synchroniser',
                      type: result.pushed > 0 ? 'success' : 'warning'
                    });
                    setTimeout(() => setCurrentEncouragement(null), 3000);
                  }}
                  disabled={google.isSyncing}
                  className="w-full flex items-center justify-center gap-3 p-3 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 hover:from-blue-600/30 hover:to-purple-600/30 rounded-xl transition-all disabled:opacity-50"
                >
                  <RefreshCw className={`w-5 h-5 text-blue-400 ${google.isSyncing ? 'animate-spin' : ''}`} />
                  <span className="text-sm font-medium text-white">
                    {google.isSyncing ? 'Synchronisation...' : 'Synchroniser avec Google Calendar'}
                  </span>
                </button>
              )}

              {/* Sync Google Tasks */}
              {!isLocalMode && google.isSignedIn && google.servicesStatus?.tasks && (
                <button
                  onClick={async () => {
                    const allTasks = Object.values(customTasks).flat();
                    const result = await google.syncLocalTasksToGoogle(allTasks);
                    if (result.synced > 0) {
                      alert(`✓ ${result.message}`);
                    } else if (result.errors?.length) {
                      alert(`Erreur: ${result.message}`);
                    } else {
                      alert(result.message);
                    }
                  }}
                  disabled={google.isSyncing}
                  className="w-full flex items-center gap-3 p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800 rounded-md transition-all active:scale-[0.98] group disabled:opacity-50"
                >
                  <div className="w-9 h-9 bg-emerald-500/10 rounded-md flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                    <CheckSquare className={`w-4 h-4 text-emerald-400 ${google.isSyncing ? 'animate-pulse' : ''}`} />
                  </div>
                  <div className="text-left flex-1">
                    <span className="text-sm font-medium text-zinc-200 block">Sync Google Tasks</span>
                    <span className="text-[11px] text-zinc-500">
                      {google.isSyncing ? 'Synchronisation...' : 'Envoyer vers Google Tasks'}
                    </span>
                  </div>
                  {google.isSyncing && <RefreshCw className="w-4 h-4 text-zinc-400 animate-spin" />}
                </button>
              )}

              {/* Génération automatique */}
              <div className="grid grid-cols-2 gap-2">
                {/* Générer une journée */}
                <button
                  onClick={() => {
                    setShowSettings(false);
                    setShowDayGenerator(true);
                  }}
                  className="flex flex-col items-center gap-2 p-3 bg-gradient-to-br from-emerald-950/40 to-cyan-950/40 border border-emerald-500/30 hover:border-emerald-500/50 rounded-xl transition-all active:scale-[0.98]"
                >
                  <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Sun className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div className="text-center">
                    <span className="text-xs font-medium text-emerald-200 block">Ma journée</span>
                    <span className="text-[9px] text-emerald-400/70">Aujourd'hui</span>
                  </div>
                </button>

                {/* Aller à la vue semaine (génération IA disponible là-bas) */}
                <button
                  onClick={() => {
                    setShowSettings(false);
                    setActiveView('weekly');
                  }}
                  className="flex flex-col items-center gap-2 p-3 bg-gradient-to-br from-cyan-950/40 to-purple-950/40 border border-cyan-500/30 hover:border-cyan-500/50 rounded-xl transition-all active:scale-[0.98]"
                >
                  <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                    <Zap className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div className="text-center">
                    <span className="text-xs font-medium text-cyan-200 block">Vue semaine</span>
                    <span className="text-[9px] text-cyan-400/70">Générer avec IA ⚡</span>
                  </div>
                </button>
              </div>

              {/* Mes templates */}
              <button
                onClick={() => {
                  setShowSettings(false);
                  setShowTemplates(true);
                }}
                className="w-full flex items-center gap-3 p-3 bg-gradient-to-r from-amber-950/30 to-orange-950/30 border border-amber-500/30 hover:border-amber-500/50 rounded-xl transition-all active:scale-[0.98] group"
              >
                <div className="w-9 h-9 bg-amber-500/20 rounded-md flex items-center justify-center group-hover:bg-amber-500/30 transition-colors">
                  <Calendar className="w-4 h-4 text-amber-400" />
                </div>
                <div className="text-left flex-1">
                  <span className="text-sm font-medium text-amber-200 block">Mes templates</span>
                  <span className="text-[11px] text-amber-400/70">
                    {customTemplates.templates.length > 0
                      ? `${customTemplates.templates.length} template${customTemplates.templates.length > 1 ? 's' : ''} sauvegardé${customTemplates.templates.length > 1 ? 's' : ''}`
                      : 'Sauvegarder ma semaine type'
                    }
                  </span>
                </div>
              </button>

              {/* Génération automatique */}
              <button
                onClick={() => {
                  setShowSettings(false);
                  setShowAutoGenSettings(true);
                }}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all active:scale-[0.98] group ${
                  autoGeneration.settings.enabled
                    ? 'bg-gradient-to-r from-emerald-950/30 to-teal-950/30 border border-emerald-500/30 hover:border-emerald-500/50'
                    : 'bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800'
                }`}
              >
                <div className={`w-9 h-9 rounded-md flex items-center justify-center transition-colors ${
                  autoGeneration.settings.enabled
                    ? 'bg-emerald-500/20 group-hover:bg-emerald-500/30'
                    : 'bg-zinc-700/50 group-hover:bg-zinc-600/50'
                }`}>
                  <RefreshCw className={`w-4 h-4 ${autoGeneration.settings.enabled ? 'text-emerald-400' : 'text-zinc-400'}`} />
                </div>
                <div className="text-left flex-1">
                  <span className={`text-sm font-medium block ${autoGeneration.settings.enabled ? 'text-emerald-200' : 'text-zinc-200'}`}>
                    Génération auto
                  </span>
                  <span className={`text-[11px] ${autoGeneration.settings.enabled ? 'text-emerald-400/70' : 'text-zinc-500'}`}>
                    {autoGeneration.settings.enabled
                      ? `Chaque ${['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][autoGeneration.settings.dayOfWeek]}`
                      : 'Planifier ma semaine automatiquement'
                    }
                  </span>
                </div>
                {autoGeneration.settings.enabled && (
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                )}
              </button>

              {/* Paramètres avancés */}
              <button
                onClick={() => {
                  setShowSettings(false);
                  setShowAdvancedSettings(true);
                }}
                className="w-full flex items-center gap-3 p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800 rounded-md transition-all active:scale-[0.98] group"
              >
                <div className="w-9 h-9 bg-purple-500/10 rounded-md flex items-center justify-center group-hover:bg-purple-500/20 transition-colors">
                  <Settings className="w-4 h-4 text-purple-400" />
                </div>
                <div className="text-left">
                  <span className="text-sm font-medium text-zinc-200 block">Paramètres avancés</span>
                  <span className="text-[11px] text-zinc-500">Adresse, horaires, services</span>
                </div>
              </button>

              {/* Export */}
              <button
                onClick={exportWeekToCSV}
                className="w-full flex items-center gap-3 p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800 rounded-md transition-all active:scale-[0.98] group"
              >
                <div className="w-9 h-9 bg-zinc-700/50 rounded-md flex items-center justify-center group-hover:bg-zinc-600/50 transition-colors">
                  <Download className="w-4 h-4 text-zinc-400" />
                </div>
                <div className="text-left">
                  <span className="text-sm font-medium text-zinc-200 block">Exporter en CSV</span>
                  <span className="text-[11px] text-zinc-500">Planning de la semaine</span>
                </div>
              </button>

              {/* Gestion des données */}
              <button
                onClick={() => {
                  setShowSettings(false);
                  setShowDataManagement(true);
                }}
                className="w-full flex items-center gap-3 p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800 rounded-md transition-all active:scale-[0.98] group"
              >
                <div className="w-9 h-9 bg-indigo-500/10 rounded-md flex items-center justify-center group-hover:bg-indigo-500/20 transition-colors">
                  <ArrowDownToLine className="w-4 h-4 text-indigo-400" />
                </div>
                <div className="text-left">
                  <span className="text-sm font-medium text-zinc-200 block">Gérer mes données</span>
                  <span className="text-[11px] text-zinc-500">Export, import, sauvegarde</span>
                </div>
              </button>

              {/* Synchronisation Cloud */}
              <button
                onClick={() => {
                  setShowSettings(false);
                  setShowCloudSync(true);
                }}
                className="w-full flex items-center gap-3 p-3 bg-[var(--bg-card,#151518)] border border-[var(--border-subtle,rgba(255,255,255,0.06))] hover:bg-zinc-800 rounded-md transition-all active:scale-[0.98] group"
              >
                <div className="w-9 h-9 bg-cyan-500/10 rounded-md flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                  <Cloud className="w-4 h-4 text-cyan-400" />
                </div>
                <div className="text-left">
                  <span className="text-sm font-medium text-zinc-200 block">Sync Cloud</span>
                  <span className="text-[11px] text-zinc-500">Firebase Firestore</span>
                </div>
              </button>
            </div>

            {/* Footer */}
            <div className="pt-3 pb-1">
              <div className="flex items-center justify-center gap-2 text-[11px] text-zinc-600">
                <Shield className="w-3 h-3" />
                <span>Données locales + Cloud Firebase</span>
              </div>
            </div>
          </div>
        </BottomSheet>


        {/* Global Upgrade Modal */}
        <UpgradeModal
          isOpen={!!upgradeFeature}
          onClose={() => setUpgradeFeature(null)}
          feature={upgradeFeature}
          userId={google?.user?.uid}
          userEmail={google?.user?.email}
          onOpenPricing={() => setActiveViewPersisted('pricing')}
        />

        {/* Bottom Navigation Bar + FAB */}
        <BottomNavBar
          daysNames={daysNames}
          weekDates={weekDates}
          currentDayIndex={currentDayIndex}
          weekOffset={weekOffset}
          onDaySelect={setCurrentDayIndex}
          onWeekChange={(delta) => setWeekOffset(p => p + delta)}
          onHomeClick={() => setActiveView('dashboard')}
          onStatsClick={() => setShowStats(true)}
          onSettingsClick={() => setShowSettings(true)}
          onTimerClick={() => setShowDeviceActions(true)}
          onSyncClick={syncGoogleCalendar}
          isSyncing={isSyncingCalendar || google.isSyncing}
          isGoogleConnected={google.isSignedIn && google.servicesStatus?.calendar}
          activeTimers={deviceActions.timers?.filter(t => !t.completed).length || 0}
          isPro={subscription.isPro}
          onAddTask={() => {
            // Rediriger vers la vue semaine pour ajouter des tâches
            if (activeView !== 'weekly') {
              setActiveView('weekly');
            }
            // La vue semaine gère l'ajout de tâches via drag & drop
          }}
          level={gameification.level}
          checkFrenchHoliday={checkFrenchHoliday}
          onSportClick={() => goSport.openWizard()}
          sportStreak={goSport.data.streak?.current || 0}
          onRewardsClick={() => rewards.setShowRewardsPanel(true)}
          rewardCoins={rewards.availableCoins}
          hasFreeReward={!!rewards.checkDailyFreeReward()}
        />

        {/* === MODALES ET OVERLAYS === */}

        {/* Modal Check-in Humeur */}
        {mood.showCheckIn && (
          <MoodCheckInModal
            onSave={(moodValue, energyValue, notes) => {
              mood.saveCheckIn(moodValue, energyValue, notes);
            }}
            onSkip={() => mood.setShowCheckIn(false)}
          />
        )}

        {/* Modal Démarrage de Tâche */}
        <TaskStartModal
          task={taskToStart}
          isOpen={!!taskToStart}
          onStart={handleTaskStart}
          onDismiss={() => setTaskToStart(null)}
          onSnooze={handleTaskSnooze}
        />

        {/* Modal Détails de Tâche */}
        <TaskDetailSheet
          task={selectedTaskDetail}
          isOpen={!!selectedTaskDetail}
          isCompleted={selectedTaskDetail?.isCompleted}
          onClose={() => setSelectedTaskDetail(null)}
          onComplete={handleCompleteTask}
          onStartFocus={(task) => setShowFocusStart(task)}
          onDelete={(taskId) => {
            removeTask(taskId);
            setSelectedTaskDetail(null);
          }}
          onViewInWeek={() => setActiveView('weekly')}
          isSubTaskCompleted={isSubTaskCompleted}
          toggleSubTask={toggleSubTask}
          getCompletedCount={getCompletedCount}
          weekOffset={weekOffset}
          currentDayIndex={currentDayIndex}
          onSetTimer={(minutes, label) => {
            deviceActions.setTimer(minutes, label);
            setSelectedTaskDetail(null);
          }}
          onSetReminder={(task) => {
            deviceActions.setTaskReminder(task);
            setSelectedTaskDetail(null);
          }}
          onPlayMusic={(type) => deviceActions.playAmbientMusic(type)}
          onOpenDeviceActions={() => {
            setSelectedTaskDetail(null);
            setShowDeviceActions(true);
          }}
        />

        {/* Actions appareil (minuteurs, alarmes, musique) */}
        <DeviceActionsSheet
          isOpen={showDeviceActions}
          onClose={() => setShowDeviceActions(false)}
          deviceActions={deviceActions}
        />

        {/* Mode Focus */}
        {focusMode.isActive && (
          <FocusModeOverlay
            task={focusMode.currentTask}
            isActive={focusMode.isActive}
            timerMode={focusMode.timerMode}
            timeRemaining={focusMode.timeRemaining}
            totalTime={focusMode.totalTime}
            isPaused={focusMode.isPaused}
            formattedTime={focusMode.formattedTime}
            progress={focusMode.progress}
            currentMessage={focusMode.currentMessage}
            preset={focusMode.preset}
            onTogglePause={focusMode.togglePause}
            onStop={focusMode.stopFocus}
            onExtend={focusMode.extendSession}
            onStartBreak={focusMode.startBreak}
            onComplete={handleCompleteFocus}
          />
        )}

        {/* Modal démarrage Focus */}
        {showFocusStart && (
          <FocusStartModal
            task={showFocusStart}
            onStart={(task, preset) => handleStartFocus(task, preset)}
            onClose={() => setShowFocusStart(null)}
          />
        )}

        {/* Galerie des victoires */}
        {showVictoryGallery && (
          <VictoryGallery
            proofs={proofs}
            onClose={() => setShowVictoryGallery(false)}
            onDelete={deleteProof}
          />
        )}

        {/* Panneau des statistiques */}
        {showStats && (
          <StatsView
            totalXP={gameification.totalXP}
            todayXP={gameification.todayXP}
            level={gameification.level}
            levelProgress={gameification.levelProgress}
            currentStreak={gameification.currentStreak}
            longestStreak={gameification.longestStreak}
            tasksCompleted={gameification.stats?.tasksCompleted || 0}
            vitalTasksCompleted={gameification.stats?.vitalTasksCompleted || 0}
            unlockedBadges={gameification.unlockedBadges}
            weeklyStats={gameification.weeklyStats}
            focusTime={gameification.stats?.focusTime}
            proofsCount={proofs.length}
            recentXPGains={gameification.stats?.recentXPGains || []}
            onClose={() => setShowStats(false)}
          />
        )}

        {/* Panneau des récompenses (Musique, Méditation, etc.) */}
        <RewardsPanel
          isOpen={rewards.showRewardsPanel}
          onClose={() => rewards.setShowRewardsPanel(false)}
          availableCoins={rewards.availableCoins}
          categories={rewards.categories}
          catalog={rewards.catalog}
          getByCategory={rewards.getByCategory}
          isUnlocked={rewards.isUnlocked}
          isFavorite={rewards.isFavorite}
          canUseReward={rewards.canUseReward}
          onUseReward={rewards.useReward}
          toggleFavorite={rewards.toggleFavorite}
          getRecommendations={rewards.getRecommendations}
          getFavorites={rewards.getFavorites}
          checkDailyFreeReward={rewards.checkDailyFreeReward}
          onUseDailyFreeReward={rewards.useDailyFreeReward}
        />

        {/* Lecteur de récompense active */}
        <RewardPlayer
          reward={rewards.activeReward}
          isPlaying={rewards.isPlaying}
          onClose={rewards.stopReward}
          onToggleFavorite={rewards.toggleFavorite}
          isFavorite={rewards.activeReward ? rewards.isFavorite(rewards.activeReward.id) : false}
        />

        {/* Mode Crise */}
        {showCrisisMode && (
          <CrisisModeOverlay onClose={() => setShowCrisisMode(false)} />
        )}

        {/* Go Sport Wizard */}
        {goSport.showWizard && (
          <GoSportWizard
            goSport={goSport}
            onClose={() => goSport.setShowWizard(false)}
          />
        )}

        {/* Modal Capture de Preuve */}
        {showProofCapture && (
          <ProofCaptureModal
            task={showProofCapture}
            onCapture={(proofData) => {
              handleProofCapture({
                ...proofData,
                taskId: showProofCapture.id,
                taskName: showProofCapture.task,
                category: showProofCapture.category || 'chore',
                date: showProofCapture.date || new Date().toISOString().split('T')[0]
              });
              setShowProofCapture(null);
            }}
            onClose={() => {
              setShowProofCapture(null);
              // Si fermeture sans preuve et tâche en attente, annuler la completion
              if (pendingTaskCompletion) {
                setPendingTaskCompletion(null);
                setCurrentEncouragement({
                  message: "⚠️ Ajoute une preuve pour terminer la tâche",
                  type: 'warning'
                });
                setTimeout(() => setCurrentEncouragement(null), 3000);
              }
            }}
          />
        )}

        {/* Animations et effets */}
        {showConfetti && <Confetti active={true} onComplete={() => setShowConfetti(false)} />}

        {currentEncouragement && (
          <EncouragementToast
            message={currentEncouragement.message}
            type={currentEncouragement.type}
          />
        )}

        {/* Toast de progression d'upload */}
        <UploadProgressToast
          upload={uploadStatus}
          onDismiss={() => setUploadStatus(null)}
        />

        {/* Toast d'erreur centralisé */}
        {toastError && (
          <div
            role="alert"
            style={{ zIndex: 9998 }}
            className="fixed bottom-[80px] left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-sm
              bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 flex items-start gap-3 shadow-xl"
          >
            <span className="text-amber-400 text-base leading-none mt-0.5">⚠</span>
            <p className="flex-1 text-sm text-zinc-200 leading-snug">{toastError.message}</p>
            <button
              onClick={dismissToast}
              className="text-zinc-500 hover:text-zinc-300 transition-colors text-lg leading-none"
            >
              ×
            </button>
          </div>
        )}

        {/* Paramètres unifiés */}
        <UnifiedSettingsSheet
          isOpen={showAdvancedSettings}
          onClose={() => setShowAdvancedSettings(false)}
          settings={settings}
          onToggleSetting={toggleSetting}
          onExportCSV={exportWeekToCSV}
          onSignOut={google.signOut}
          onRefreshCalendar={handleSyncTasks}
          onSyncGoogleTasks={handleSyncTasks}
          isLocalMode={isLocalMode}
          isGoogleConnected={google.isSignedIn}
          isTasksAvailable={google.servicesStatus?.tasks}
          isSyncing={google.isSyncing}
          onGenerateWeek={(weekTasks) => {
            // Appliquer les tâches générées SANS dupliquer les existantes
            let added = 0;
            let skipped = 0;
            Object.entries(weekTasks).forEach(([dateStr, tasks]) => {
              const existingForDay = getTasksForDate(dateStr);
              const existingNames = existingForDay.map(t => (t.task || t.title || '').toLowerCase());

              tasks.forEach(task => {
                const taskName = (task.task || task.title || '').toLowerCase();
                // Skip si une tache avec le meme nom existe deja ce jour
                if (existingNames.includes(taskName)) {
                  skipped++;
                  return;
                }
                addTask({
                  ...task,
                  date: dateStr
                });
                added++;
              });
            });
            setCurrentEncouragement({
              message: `🎉 ${added} taches ajoutees${skipped > 0 ? ` (${skipped} existantes conservees)` : ''} !`,
              type: 'success'
            });
          }}
          ttsSettings={ttsSettings}
          onUpdateTTS={updateTTS}
          ttsVoices={voices}
          existingTasks={customTasks}
          onResetTasks={() => {
            clearAllTasks();
            agendaEvents.clearAllEvents();
            agendaEvents.resetSetup();
            setShowAdvancedSettings(false);
            // Ouvrir le wizard de configuration
            setShowWeekSetupWizard(true);
          }}
          onResetAgendaSetup={() => {
            agendaEvents.resetSetup();
            setActiveView('weekly');
          }}
        />

        {/* Résolution de conflits de synchronisation */}
        <ConflictResolutionSheet
          isOpen={showConflictSheet}
          onClose={() => setShowConflictSheet(false)}
          conflicts={conflicts}
          onKeepLocal={(entityId, entityType, localData) => {
            if (entityType === 'task') saveTaskFirestore(localData).catch(() => {});
            else saveAgendaEventFirestore(localData).catch(() => {});
            resolveConflict(entityId);
          }}
          onKeepRemote={(entityId) => resolveConflict(entityId)}
          onDismissConflict={(entityId) => resolveConflict(entityId)}
        />

        {/* Générateur de journée */}
        <DayGeneratorSheet
          isOpen={showDayGenerator}
          onClose={() => setShowDayGenerator(false)}
          selectedDate={selectedDate}
          isWeekend={isWeekend}
          onGenerate={(dayTasks, dateStr) => {
            // Ajouter les tâches générées
            dayTasks.forEach(task => {
              addTask({
                ...task,
                date: dateStr
              });
            });

            setCurrentEncouragement({
              message: '🎉 Ta journée a été générée !',
              type: 'success'
            });
            setTimeout(() => setCurrentEncouragement(null), 3000);
          }}
          existingTasks={getTasksForDate(selectedDate ? formatDateKey(selectedDate) : '')}
        />

        {gameification.showXPGain && (
          <XPGainAnimation
            amount={gameification.lastXPGain}
            onComplete={() => gameification.clearXPGain?.()}
          />
        )}

        {gameification.showLevelUp && (
          <LevelUpAnimation
            level={gameification.level}
            onComplete={() => gameification.clearLevelUp?.()}
          />
        )}

        {gameification.newBadge && (
          <BadgeUnlockAnimation
            badge={gameification.newBadge}
            onComplete={() => gameification.clearNewBadge?.()}
          />
        )}

        {/* Setup Wizard - Première utilisation */}
        <SetupWizard
          isOpen={showSetupWizard}
          onClose={() => {
            setShowSetupWizard(false);
            localStorage.setItem('adhd_setup_completed', 'true');
          }}
          onComplete={handleSetupComplete}
        />

        {/* Templates personnalisés */}
        <TemplatesSheet
          isOpen={showTemplates}
          onClose={() => setShowTemplates(false)}
          templates={customTemplates.templates}
          onSaveTemplate={customTemplates.saveTemplate}
          onApplyTemplate={handleApplyTemplate}
          onDeleteTemplate={customTemplates.deleteTemplate}
          onDuplicateTemplate={customTemplates.duplicateTemplate}
          currentWeekTasks={customTasks}
          weekDates={weekDates}
        />

        {/* Génération automatique - Prompt */}
        <AutoGenerationPrompt
          isOpen={autoGeneration.showPrompt}
          onConfirm={handleConfirmAutoGeneration}
          onCancel={autoGeneration.cancelGeneration}
          onPostpone={autoGeneration.cancelGeneration}
          settings={autoGeneration.settings}
        />

        {/* Génération automatique - Settings */}
        <AutoGenerationSettings
          isOpen={showAutoGenSettings}
          onClose={() => setShowAutoGenSettings(false)}
          settings={autoGeneration.settings}
          onUpdateSettings={autoGeneration.updateSettings}
          customTemplates={customTemplates.templates}
        />

        {/* Gestion des données */}
        <DataManagementSheet
          isOpen={showDataManagement}
          onClose={() => setShowDataManagement(false)}
        />

        {/* Synchronisation Cloud */}
        <CloudSyncSheet
          isOpen={showCloudSync}
          onClose={() => setShowCloudSync(false)}
        />

        {/* Event Editor Sheet - Édition des événements de l'agenda */}
        <EventEditorSheet
          isOpen={showEventEditor}
          event={editingAgendaEvent}
          onClose={() => { setShowEventEditor(false); setEditingAgendaEvent(null); }}
          onSave={(updated) => {
            if (updated.id && agendaEvents.events.some(e => e.id === updated.id)) {
              agendaEvents.updateEvent(updated.id, updated);
            } else {
              agendaEvents.addEvent(updated);
            }
            setShowEventEditor(false);
            setEditingAgendaEvent(null);
          }}
          onDelete={(eventId) => {
            agendaEvents.deleteEvent(eventId);
            setShowEventEditor(false);
            setEditingAgendaEvent(null);
          }}
        />

        {/* Week Setup Wizard - Configuration de la semaine */}
        <WeekSetupWizard
          isOpen={showWeekSetupWizard}
          onClose={() => setShowWeekSetupWizard(false)}
          onComplete={(events) => {
            // Sauvegarder les événements générés
            if (events && events.length > 0) {
              agendaEvents.setEvents(events);
            }
            agendaEvents.markSetupDone();
            setActiveView('weekly');
            setShowWeekSetupWizard(false);
            setCurrentEncouragement({
              message: '🎉 Ta semaine est prête !',
              type: 'success'
            });
            setTimeout(() => setCurrentEncouragement(null), 3000);
          }}
        />
      </div>
    </div>
    </AppProvider>
  );
}

export default App;
